// screens/business/InvoiceSystemScreen.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  ScrollView,
  RefreshControl,
  StatusBar,
  Alert,
  Modal,
  FlatList,
  ActivityIndicator,
  TextInput,
  Platform,
  Share,
  Image,
} from 'react-native';
import {
  MaterialCommunityIcons,
  Ionicons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImagePicker from 'expo-image-picker';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import AsyncStorage from '@react-native-async-storage/async-storage';

import { useBusiness } from '../../../context/BusinessContext';

const { width, height } = Dimensions.get('window');

// API Configuration - Update with your server IP and port
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY      = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const API_TIMEOUT  = 30000;

const DEFAULT_HEADERS = {
  'Accept':       'application/json',
  'Content-Type': 'application/json',
  'X-API-Key':    API_KEY,
};

// Returns headers with live auth token
const getAuthHeaders = async () => {
  const tok = await AsyncStorage.getItem('auth_token')
            || await AsyncStorage.getItem('token') || '';
  return { ...DEFAULT_HEADERS, ...(tok ? { Authorization: `Bearer ${tok}` } : {}) };
};

// Resolve company ID from all possible AsyncStorage keys
const getCompanyId = async (fallback = null) => {
  const keys = ['company_id', 'company_id'];
  for (const k of keys) {
    const v = await AsyncStorage.getItem(k);
    if (v) return v;
  }
  try {
    const cp = await AsyncStorage.getItem('company_profile');
    if (cp) { const p = JSON.parse(cp); if (p.id) return String(p.id); }
  } catch {}
  return fallback;
};

// Custom hook for API calls
const useApi = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = useCallback(async (endpoint, options = {}) => {
    if (!API_BASE_URL) {
      throw new Error('API disabled - using local data only');
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), options.timeout || API_TIMEOUT);
      
      const url = `${API_BASE_URL}${endpoint}`;
      
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers: { ...DEFAULT_HEADERS, ...options.headers },
        body: options.body,
        signal: controller.signal,
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      
      const data = await response.json();
      setIsLoading(false);
      return data;
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
      throw err;
    }
  }, []);

  return { apiCall, isLoading, error };
};

// Utility function to fix invalid dates
const fixInvalidDates = (invoices) => {
  if (!invoices || !Array.isArray(invoices)) return [];
  
  return invoices.map(invoice => {
    const fixed = { ...invoice };
    const now = new Date();
    const thirtyDaysLater = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
    
    // Fix issueDate
    if (fixed.issueDate) {
      try {
        const date = new Date(fixed.issueDate);
        if (isNaN(date.getTime())) {
          fixed.issueDate = now.toISOString();
        }
      } catch {
        fixed.issueDate = now.toISOString();
      }
    } else {
      fixed.issueDate = now.toISOString();
    }
    
    // Fix dueDate
    if (fixed.dueDate) {
      try {
        const date = new Date(fixed.dueDate);
        if (isNaN(date.getTime())) {
          fixed.dueDate = thirtyDaysLater.toISOString();
        }
      } catch {
        fixed.dueDate = thirtyDaysLater.toISOString();
      }
    } else {
      fixed.dueDate = thirtyDaysLater.toISOString();
    }
    
    // Fix paidDate if exists
    if (fixed.paidDate) {
      try {
        const date = new Date(fixed.paidDate);
        if (isNaN(date.getTime())) {
          fixed.paidDate = null;
        }
      } catch {
        fixed.paidDate = null;
      }
    }
    
    return fixed;
  });
};

export default function InvoiceSystemScreen({ navigation, route }) {
  // Route params from BusinessDashboard / FinancialManagement
  const { companyProfile: routeCompanyProfile, companyId: routeCompanyId } = route?.params || {};

  // Use Business Context
  const {
    companyProfile: contextCompanyProfile,
    updateCompanyProfile,
    addNotification,
    isOnline,
    apiAvailable,
    syncStatus
  } = useBusiness();

  const { apiCall, isLoading: apiLoading, error: apiError } = useApi();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('invoices');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showOCRSModal, setShowOCRSModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [processingOCR, setProcessingOCR] = useState(false);

  // Local company profile state (sync with context)
  const [companyProfile, setCompanyProfile] = useState({
    id: routeCompanyId || contextCompanyProfile?.id || '1',
    name: routeCompanyProfile?.name || routeCompanyProfile?.company_name || contextCompanyProfile?.name || 'Your Company Name',
    email: routeCompanyProfile?.email || contextCompanyProfile?.email || 'contact@yourcompany.com',
    phone: routeCompanyProfile?.phone || contextCompanyProfile?.phone || '+44 20 1234 5678',
    headquarters: routeCompanyProfile?.headquarters || contextCompanyProfile?.headquarters || contextCompanyProfile?.address || '',
    vatNumber: routeCompanyProfile?.vatNumber || routeCompanyProfile?.vat_number || contextCompanyProfile?.vatNumber || '',
    registrationNumber: routeCompanyProfile?.registrationNumber || routeCompanyProfile?.registration_number || contextCompanyProfile?.registrationNumber || '',
    logo: routeCompanyProfile?.logo || contextCompanyProfile?.logo || null,
    bankName: routeCompanyProfile?.bankName || routeCompanyProfile?.bank_name || contextCompanyProfile?.bankName || '',
    bankAccount: routeCompanyProfile?.bankAccount || routeCompanyProfile?.bank_account || contextCompanyProfile?.bankAccount || '',
    bankSortCode: routeCompanyProfile?.bankSortCode || routeCompanyProfile?.bank_sort_code || contextCompanyProfile?.bankSortCode || '',
    bankIBAN: routeCompanyProfile?.bankIBAN || routeCompanyProfile?.bank_iban || contextCompanyProfile?.bankIBAN || '',
    bankSWIFT: routeCompanyProfile?.bankSWIFT || routeCompanyProfile?.bank_swift || contextCompanyProfile?.bankSWIFT || '',
    // Invoice settings
    paymentTerms: contextCompanyProfile?.paymentTerms || '30',
    defaultTaxRate: contextCompanyProfile?.defaultTaxRate || '20',
    invoicePrefix: contextCompanyProfile?.invoicePrefix || 'INV',
    invoiceFooter: contextCompanyProfile?.invoiceFooter || 'Thank you for your business. Payment is due within 30 days.',
    invoiceNotes: contextCompanyProfile?.invoiceNotes || 'All charges are in GBP. Please quote invoice number with payment.',
  });

  // Data states
  const [invoices, setInvoices] = useState([]);
  const [invoiceTemplates, setInvoiceTemplates] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState({
    totalInvoices: 0,
    paidInvoices: 0,
    pendingInvoices: 0,
    overdueInvoices: 0,
    draftInvoices: 0,
    sentInvoices: 0,
    totalValue: 0,
    paidValue: 0,
    pendingValue: 0,
    overdueValue: 0,
    avgInvoiceValue: 0,
    avgPaymentTime: 13
  });

  // Sync company profile — route params first, then context
  useEffect(() => {
    if (routeCompanyProfile) {
      applyCompanyProfile(routeCompanyProfile);
    } else if (contextCompanyProfile) {
      applyCompanyProfile(contextCompanyProfile);
    }
  }, [contextCompanyProfile, routeCompanyProfile]);

  // Safe date formatting function
  const safeDate = (dateString) => {
    if (!dateString) return new Date();
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return new Date();
      }
      return date;
    } catch {
      return new Date();
    }
  };

  // Invoice form data with safe dates
  const getDefaultDates = () => {
    const now = new Date();
    const dueDate = new Date(now.getTime() + parseInt(companyProfile.paymentTerms || 30) * 24 * 60 * 60 * 1000);
    return {
      issueDate: now.toISOString().split('T')[0],
      dueDate: dueDate.toISOString().split('T')[0]
    };
  };

  const [invoiceForm, setInvoiceForm] = useState({
    clientName: '',
    clientEmail: '',
    clientCompany: '',
    clientAddress: '',
    clientVAT: '',
    invoiceNumber: '',
    issueDate: getDefaultDates().issueDate,
    dueDate: getDefaultDates().dueDate,
    description: '',
    items: [{ id: '1', description: '', quantity: 1, rate: 0, amount: 0, unit: 'hr' }],
    notes: '',
    taxRate: companyProfile.defaultTaxRate || '20',
    discountRate: '0',
    reference: '',
    paymentMethod: 'Bank Transfer'
  });

  // OCR result state
  const [ocrResult, setOcrResult] = useState(null);

  // Initialize screen
  useEffect(() => {
    initializeScreen();
  }, []);

  // Update invoice form when company profile changes
  useEffect(() => {
    setInvoiceForm(prev => ({
      ...prev,
      taxRate: companyProfile.defaultTaxRate || '20',
      issueDate: getDefaultDates().issueDate,
      dueDate: getDefaultDates().dueDate
    }));
  }, [companyProfile.defaultTaxRate, companyProfile.paymentTerms]);

  // Log API errors
  useEffect(() => {
    if (apiError) {
      console.warn('API Error (non-critical):', apiError);
    }
  }, [apiError]);

  const initializeScreen = async () => {
    try {
      setIsInitialLoading(true);

      // Step 1: Load company profile — AsyncStorage first, then API
      await loadCompanyProfile();

      // Step 2: Fetch invoices filtered by company
      await Promise.allSettled([
        fetchInvoicesFromAPI(),
        fetchTemplatesFromAPI()
      ]);
      
      // Load fallback data if needed
      if (invoices.length === 0) {
        const sampleInvoices = loadSampleInvoices();
        const fixedSampleInvoices = fixInvalidDates(sampleInvoices);
        setInvoices(fixedSampleInvoices);
      }
      
      if (invoiceTemplates.length === 0) {
        loadAllInvoiceTemplates();
      }
      
      calculateInvoiceStatistics();
      
    } catch (error) {
      console.error('Error initializing screen:', error);
    } finally {
      setIsInitialLoading(false);
    }
  };

  // Load real company profile from AsyncStorage → DB → context (in priority order)
  const loadCompanyProfile = async () => {
    try {
      // 1. Try AsyncStorage first (fastest)
      const cached = await AsyncStorage.getItem('company_profile');
      if (cached) {
        const p = JSON.parse(cached);
        applyCompanyProfile(p);
        console.log(`[InvoiceScreen] Company loaded from cache: ${p.name || p.company_name}`);
      }

      // 2. Fetch fresh from API using company ID
      const cid = await getCompanyId(companyProfile.id);
      if (!cid) return;

      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE_URL}/api/company-profile/${cid}`, { headers });
      if (!res.ok) return;
      const data = await res.json();

      const profile = data.data || data;
      if (profile) {
        applyCompanyProfile(profile);
        // Update cache
        await AsyncStorage.setItem('company_profile', JSON.stringify(profile));
        console.log(`[InvoiceScreen] Company loaded from API: ${profile.name || profile.company_name}`);
      }
    } catch (e) {
      console.warn('[InvoiceScreen] loadCompanyProfile:', e.message);
    }
  };

  // Map DB/API fields to local companyProfile state
  const applyCompanyProfile = (p) => {
    if (!p) return;
    setCompanyProfile(prev => ({
      ...prev,
      id:                 String(p.id || prev.id),
      name:               p.name || p.company_name || p.companyName || prev.name,
      email:              p.email || p.company_email || prev.email,
      phone:              p.phone || p.company_phone || prev.phone,
      headquarters:       p.headquarters || p.address || p.company_address || prev.headquarters,
      vatNumber:          p.vatNumber || p.vat_number || p.company_vat || prev.vatNumber,
      registrationNumber: p.registrationNumber || p.registration_number || prev.registrationNumber,
      logo:               (() => {
        const l = p.logo || p.logo_url || p.company_logo || p.banner_image_url || prev.logo;
        if (!l || typeof l !== 'string') return prev.logo;
        if (l.startsWith('http') || l.startsWith('data:')) return l;
        if (l.startsWith('/')) return `${API_BASE_URL}${l}`;
        return prev.logo;
      })(),
      bankName:           p.bankName || p.bank_name || prev.bankName,
      bankAccount:        p.bankAccount || p.bank_account || prev.bankAccount,
      bankSortCode:       p.bankSortCode || p.bank_sort_code || prev.bankSortCode,
      bankIBAN:           p.bankIBAN || p.bank_iban || prev.bankIBAN,
      bankSWIFT:          p.bankSWIFT || p.bank_swift || prev.bankSWIFT,
      paymentTerms:       String(p.paymentTerms || p.payment_terms || prev.paymentTerms),
      defaultTaxRate:     String(p.defaultTaxRate || p.default_tax_rate || prev.defaultTaxRate),
      invoicePrefix:      p.invoicePrefix || p.invoice_prefix || prev.invoicePrefix,
      invoiceFooter:      p.invoiceFooter || p.invoice_footer || prev.invoiceFooter,
      invoiceNotes:       p.invoiceNotes || p.invoice_notes || prev.invoiceNotes,
    }));
  };

  // API Fetch Functions
  const fetchInvoicesFromAPI = async () => {
    if (!API_BASE_URL) return;
    try {
      const cid = await getCompanyId(companyProfile.id);
      if (!cid) { console.warn('[InvoiceScreen] No company ID — skipping fetch'); return; }
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/data/financial_invoices?company_id=${cid}`, { headers });
      const data = await response.json();
      
      if (data.success && data.data) {
        const transformedInvoices = data.data.map(item => ({
          id: item.id?.toString() || Date.now().toString(),
          invoiceNumber: item.invoice_number || item.invoiceNumber || '',
          clientName: item.client_name || item.clientName || '',
          clientEmail: item.client_email || item.clientEmail || '',
          clientCompany: item.client_company || item.clientCompany || '',
          clientAddress: item.client_address || item.clientAddress || '',
          clientVAT: item.client_vat || item.clientVAT || '',
          issueDate: item.issue_date || item.issueDate || new Date().toISOString(),
          dueDate: item.due_date || item.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          paidDate: item.paid_date || item.paidDate || null,
          status: item.status || 'draft',
          description: item.description || '',
          items: item.items || [],
          subtotal: parseFloat(item.subtotal) || 0,
          taxRate: parseFloat(item.tax_rate || item.taxRate) || 20,
          taxAmount: parseFloat(item.tax_amount || item.taxAmount) || 0,
          discountRate: parseFloat(item.discount_rate || item.discountRate) || 0,
          discountAmount: parseFloat(item.discount_amount || item.discountAmount) || 0,
          total: parseFloat(item.total) || 0,
          notes: item.notes || '',
          paymentMethod: item.payment_method || item.paymentMethod || 'Bank Transfer',
          reference: item.reference || ''
        }));
        
        const fixedInvoices = fixInvalidDates(transformedInvoices);
        setInvoices(fixedInvoices);
        calculateInvoiceStatistics();
        return fixedInvoices;
      }
    } catch (error) {
      console.warn('Could not fetch invoices from API:', error.message);
    }
  };

  const fetchTemplatesFromAPI = async () => {
    if (!API_BASE_URL) return;
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/data/financial_invoice_templates?company_id=${await getCompanyId(companyProfile.id) || '1'}`, { headers });
      const data = await response.json();
      
      if (data.success && data.data) {
        const transformedTemplates = data.data.map(item => ({
          id: item.id?.toString() || Date.now().toString(),
          name: item.name || '',
          description: item.description || '',
          category: item.category || 'Other',
          icon: item.icon || 'file-document',
          items: item.items || [],
          taxRate: parseFloat(item.tax_rate || item.taxRate) || 20,
          discountRate: parseFloat(item.discount_rate || item.discountRate) || 0,
          notes: item.notes || '',
          paymentTerms: item.payment_terms || item.paymentTerms || '30'
        }));
        
        setInvoiceTemplates(transformedTemplates);
        return transformedTemplates;
      }
    } catch (error) {
      console.warn('Could not fetch templates from API:', error.message);
    }
  };

  // Load sample invoices
  const loadSampleInvoices = () => {
    const now = new Date();
    const prefix = companyProfile.invoicePrefix || 'INV';
    const taxRate = parseFloat(companyProfile.defaultTaxRate) || 20;
    
    return [
      {
        id: '1',
        invoiceNumber: `${prefix}-${now.getFullYear()}-001`,
        clientName: 'John Smith',
        clientEmail: 'john.smith@techcorp.com',
        clientCompany: 'TechCorp Ltd',
        clientAddress: '123 Business Street, London, EC1A 1BB, UK',
        clientVAT: 'GB123456789',
        issueDate: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000).toISOString(),
        paidDate: null,
        status: 'sent',
        description: 'Consulting Services - January 2024',
        items: [
          { id: '1', description: 'Senior Consultant', quantity: 20, rate: 150, amount: 3000, unit: 'hr' },
          { id: '2', description: 'Project Management', quantity: 10, rate: 200, amount: 2000, unit: 'hr' }
        ],
        subtotal: 5000,
        taxRate: taxRate,
        taxAmount: 5000 * (taxRate / 100),
        discountRate: 0,
        discountAmount: 0,
        total: 5000 + (5000 * (taxRate / 100)),
        notes: 'Monthly consulting services',
        paymentMethod: 'Bank Transfer',
        reference: 'PO-2024-001'
      },
      {
        id: '2',
        invoiceNumber: `${prefix}-${now.getFullYear()}-002`,
        clientName: 'Sarah Johnson',
        clientEmail: 'sarah.j@greenbuilders.com',
        clientCompany: 'Green Builders Ltd',
        clientAddress: '456 Eco Avenue, Manchester, M1 2AB, UK',
        clientVAT: 'GB987654321',
        issueDate: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
        dueDate: new Date(now.getTime() + 25 * 24 * 60 * 60 * 1000).toISOString(),
        paidDate: null,
        status: 'draft',
        description: 'Software Development - Phase 2',
        items: [
          { id: '1', description: 'Frontend Development', quantity: 40, rate: 85, amount: 3400, unit: 'hr' },
          { id: '2', description: 'Backend API', quantity: 30, rate: 95, amount: 2850, unit: 'hr' },
          { id: '3', description: 'Database Design', quantity: 15, rate: 110, amount: 1650, unit: 'hr' }
        ],
        subtotal: 7900,
        taxRate: taxRate,
        taxAmount: 7900 * (taxRate / 100),
        discountRate: 5,
        discountAmount: 395,
        total: 7900 + (7900 * (taxRate / 100)) - 395,
        notes: 'Development services for Q1 2024',
        paymentMethod: 'Bank Transfer',
        reference: 'PO-2024-002'
      }
    ];
  };

  // Load ALL invoice templates with fixed dates
  const loadAllInvoiceTemplates = () => {
    const allTemplates = [
      // Professional Services
      {
        id: '1',
        name: 'Consulting Services',
        description: 'Professional consulting and advisory services',
        category: 'Professional Services',
        icon: 'account-tie',
        items: [
          { description: 'Strategy Consulting', quantity: 1, rate: 250, unit: 'hour' },
          { description: 'Business Analysis', quantity: 1, rate: 180, unit: 'hour' },
          { description: 'Project Management', quantity: 1, rate: 200, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Consulting services provided as per agreement. Payment terms: Net 30 days.',
        paymentTerms: companyProfile.paymentTerms || '30'
      },
      {
        id: '2',
        name: 'Legal Services',
        description: 'Legal consultation and documentation',
        category: 'Professional Services',
        icon: 'gavel',
        items: [
          { description: 'Legal Consultation', quantity: 1, rate: 300, unit: 'hour' },
          { description: 'Contract Review', quantity: 1, rate: 250, unit: 'hour' },
          { description: 'Document Drafting', quantity: 1, rate: 200, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Legal services rendered. Payment due upon receipt.',
        paymentTerms: '15'
      },
      {
        id: '3',
        name: 'Accounting Services',
        description: 'Professional accounting and bookkeeping',
        category: 'Professional Services',
        icon: 'calculator-variant',
        items: [
          { description: 'Bookkeeping Services', quantity: 1, rate: 65, unit: 'hour' },
          { description: 'Tax Preparation', quantity: 1, rate: 150, unit: 'hour' },
          { description: 'Financial Statement Review', quantity: 1, rate: 200, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Monthly accounting services. Direct debit available.',
        paymentTerms: '30'
      },

      // IT & Development
      {
        id: '4',
        name: 'Software Development',
        description: 'Custom software development services',
        category: 'IT & Development',
        icon: 'code-braces',
        items: [
          { description: 'Frontend Development', quantity: 1, rate: 85, unit: 'hour' },
          { description: 'Backend Development', quantity: 1, rate: 95, unit: 'hour' },
          { description: 'Database Design', quantity: 1, rate: 100, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Development services as per sprint backlog. Invoiced weekly.',
        paymentTerms: '14'
      },
      {
        id: '5',
        name: 'Web Design & Development',
        description: 'Website design and development',
        category: 'IT & Development',
        icon: 'web',
        items: [
          { description: 'UI/UX Design', quantity: 1, rate: 75, unit: 'hour' },
          { description: 'Frontend Development', quantity: 1, rate: 85, unit: 'hour' },
          { description: 'CMS Integration', quantity: 1, rate: 90, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Website development project. Milestone based billing.',
        paymentTerms: '30'
      },
      {
        id: '6',
        name: 'IT Support & Maintenance',
        description: 'Ongoing IT support and maintenance',
        category: 'IT & Development',
        icon: 'desktop-classic',
        items: [
          { description: 'Monthly Support Retainer', quantity: 1, rate: 1000, unit: 'month' },
          { description: 'Emergency Support', quantity: 1, rate: 120, unit: 'hour' },
          { description: 'System Updates', quantity: 1, rate: 80, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Monthly IT support retainer. 24/7 emergency support available.',
        paymentTerms: '30'
      },
      {
        id: '7',
        name: 'Cloud Services',
        description: 'Cloud infrastructure and services',
        category: 'IT & Development',
        icon: 'cloud',
        items: [
          { description: 'Cloud Server Hosting', quantity: 1, rate: 200, unit: 'month' },
          { description: 'Storage Services', quantity: 100, rate: 0.10, unit: 'GB' },
          { description: 'CDN Bandwidth', quantity: 100, rate: 0.05, unit: 'GB' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Monthly cloud infrastructure costs. Usage-based billing.',
        paymentTerms: '30'
      },

      // Creative Services
      {
        id: '8',
        name: 'Graphic Design',
        description: 'Professional graphic design services',
        category: 'Creative Services',
        icon: 'palette',
        items: [
          { description: 'Logo Design', quantity: 1, rate: 500, unit: 'project' },
          { description: 'Brand Identity', quantity: 1, rate: 1200, unit: 'project' },
          { description: 'Print Design', quantity: 1, rate: 75, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Creative design services. Revisions included as per agreement.',
        paymentTerms: '15'
      },
      {
        id: '9',
        name: 'Content Writing',
        description: 'Professional content creation',
        category: 'Creative Services',
        icon: 'pencil',
        items: [
          { description: 'Blog Posts', quantity: 1, rate: 100, unit: 'post' },
          { description: 'SEO Articles', quantity: 1, rate: 120, unit: 'article' },
          { description: 'Copywriting', quantity: 1, rate: 70, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Content creation services. Unlimited revisions.',
        paymentTerms: '30'
      },
      {
        id: '10',
        name: 'Video Production',
        description: 'Professional video production',
        category: 'Creative Services',
        icon: 'video',
        items: [
          { description: 'Filming Services', quantity: 1, rate: 150, unit: 'hour' },
          { description: 'Video Editing', quantity: 1, rate: 100, unit: 'hour' },
          { description: 'Motion Graphics', quantity: 1, rate: 120, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Video production services. Raw footage included.',
        paymentTerms: '50% deposit required'
      },

      // Marketing
      {
        id: '11',
        name: 'Digital Marketing',
        description: 'Comprehensive digital marketing',
        category: 'Marketing',
        icon: 'bullhorn',
        items: [
          { description: 'SEO Services', quantity: 1, rate: 800, unit: 'month' },
          { description: 'PPC Management', quantity: 1, rate: 600, unit: 'month' },
          { description: 'Social Media Management', quantity: 1, rate: 500, unit: 'month' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Monthly digital marketing retainer. Ad spend billed separately.',
        paymentTerms: '30'
      },
      {
        id: '12',
        name: 'SEO Services',
        description: 'Search engine optimization',
        category: 'Marketing',
        icon: 'google',
        items: [
          { description: 'Keyword Research', quantity: 1, rate: 300, unit: 'project' },
          { description: 'On-Page SEO', quantity: 1, rate: 200, unit: 'hour' },
          { description: 'Link Building', quantity: 1, rate: 150, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'SEO optimization services. Monthly progress reports included.',
        paymentTerms: '30'
      },
      {
        id: '13',
        name: 'Social Media Marketing',
        description: 'Social media management',
        category: 'Marketing',
        icon: 'facebook',
        items: [
          { description: 'Content Creation', quantity: 1, rate: 60, unit: 'hour' },
          { description: 'Community Management', quantity: 1, rate: 45, unit: 'hour' },
          { description: 'Paid Advertising', quantity: 1, rate: 75, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Social media marketing services. Ad budget billed separately.',
        paymentTerms: '30'
      },

      // Products
      {
        id: '14',
        name: 'Product Sale',
        description: 'Physical product sales',
        category: 'Products',
        icon: 'package-variant',
        items: [
          { description: 'Product - Standard Item', quantity: 1, rate: 99.99, unit: 'unit' },
          { description: 'Shipping & Handling', quantity: 1, rate: 15.00, unit: 'order' },
          { description: 'Installation Service', quantity: 1, rate: 49.99, unit: 'hour' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Product sale. Shipping costs included. Returns accepted within 30 days.',
        paymentTerms: 'Due upon receipt'
      },
      {
        id: '15',
        name: 'Equipment Rental',
        description: 'Equipment and machinery rental',
        category: 'Products',
        icon: 'excavator',
        items: [
          { description: 'Heavy Equipment Rental', quantity: 1, rate: 350.00, unit: 'day' },
          { description: 'Delivery & Pickup', quantity: 1, rate: 75.00, unit: 'trip' },
          { description: 'Insurance Coverage', quantity: 1, rate: 25.00, unit: 'day' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Equipment rental charges. Security deposit of £500 required.',
        paymentTerms: '50% upfront'
      },

      // Services
      {
        id: '16',
        name: 'Maintenance Services',
        description: 'Ongoing maintenance contracts',
        category: 'Services',
        icon: 'wrench',
        items: [
          { description: 'Preventive Maintenance Visit', quantity: 1, rate: 180.00, unit: 'visit' },
          { description: 'Emergency Repairs', quantity: 1, rate: 120.00, unit: 'hour' },
          { description: 'Parts Replacement', quantity: 1, rate: 45.00, unit: 'item' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Annual maintenance contract. 24/7 emergency service available.',
        paymentTerms: 'Annual prepayment'
      },

      // Construction
      {
        id: '17',
        name: 'Construction Services',
        description: 'Construction and renovation',
        category: 'Construction',
        icon: 'hammer',
        items: [
          { description: 'Skilled Labor', quantity: 1, rate: 45.00, unit: 'hour' },
          { description: 'Construction Materials', quantity: 1, rate: 250.00, unit: 'lot' },
          { description: 'Equipment Rental', quantity: 1, rate: 150.00, unit: 'day' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Construction services. Payment based on milestone completion.',
        paymentTerms: 'Net 30'
      },

      // Engineering
      {
        id: '18',
        name: 'Engineering Services',
        description: 'Professional engineering services',
        category: 'Engineering',
        icon: 'engine',
        items: [
          { description: 'Design Engineering', quantity: 1, rate: 95.00, unit: 'hour' },
          { description: 'Site Survey & Assessment', quantity: 1, rate: 350.00, unit: 'visit' },
          { description: 'Technical Drawing', quantity: 1, rate: 85.00, unit: 'drawing' }
        ],
        taxRate: parseFloat(companyProfile.defaultTaxRate) || 20,
        discountRate: 0,
        notes: 'Engineering consultancy services. Professional indemnity insurance held.',
        paymentTerms: 'Net 30'
      }
    ];
    
    // Update all templates with company tax rate
    const taxRate = parseFloat(companyProfile.defaultTaxRate) || 20;
    const updatedTemplates = allTemplates.map(template => ({
      ...template,
      taxRate: taxRate,
      items: template.items.map(item => ({
        ...item,
        rate: item.rate // Keep the default rates
      }))
    }));
    
    setInvoiceTemplates(updatedTemplates);
  };

  const calculateInvoiceStatistics = () => {
    const stats = {
      totalInvoices: invoices.length,
      paidInvoices: invoices.filter(inv => inv.status === 'paid').length,
      pendingInvoices: invoices.filter(inv => inv.status === 'pending').length,
      overdueInvoices: invoices.filter(inv => inv.status === 'overdue').length,
      draftInvoices: invoices.filter(inv => inv.status === 'draft').length,
      sentInvoices: invoices.filter(inv => inv.status === 'sent').length,
      totalValue: invoices.reduce((sum, inv) => sum + (inv.total || 0), 0),
      paidValue: invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.total || 0), 0),
      pendingValue: invoices.filter(inv => ['sent', 'pending', 'overdue'].includes(inv.status))
        .reduce((sum, inv) => sum + (inv.total || 0), 0),
      overdueValue: invoices.filter(inv => inv.status === 'overdue')
        .reduce((sum, inv) => sum + (inv.total || 0), 0),
      avgInvoiceValue: invoices.length > 0 
        ? invoices.reduce((sum, inv) => sum + (inv.total || 0), 0) / invoices.length 
        : 0,
      avgPaymentTime: 13
    };
    
    setInvoiceStats(stats);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    
    await Promise.allSettled([
      fetchInvoicesFromAPI(),
      fetchTemplatesFromAPI()
    ]);
    
    if (invoices.length === 0) {
      const sampleInvoices = loadSampleInvoices();
      const fixedSampleInvoices = fixInvalidDates(sampleInvoices);
      setInvoices(fixedSampleInvoices);
    }
    
    if (invoiceTemplates.length === 0) {
      loadAllInvoiceTemplates();
    }
    
    calculateInvoiceStatistics();
    setRefreshing(false);
  };

  // Local invoice management
  const saveInvoice = async (invoiceData) => {
    if (API_BASE_URL) {
      try {
        const cid     = await getCompanyId(companyProfile.id);
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/api/data/financial_invoices`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            company_id:      cid ? parseInt(cid) : null,
            company_id:     cid ? parseInt(cid) : null,
            invoice_number:  invoiceData.invoiceNumber,
            client_name:     invoiceData.clientName,
            client_email:    invoiceData.clientEmail,
            client_company:  invoiceData.clientCompany,
            client_address:  invoiceData.clientAddress,
            client_vat:      invoiceData.clientVAT,
            issue_date:      invoiceData.issueDate.split('T')[0],
            due_date:        invoiceData.dueDate.split('T')[0],
            description:     invoiceData.description,
            items:           invoiceData.items,
            subtotal:        invoiceData.subtotal,
            tax_rate:        invoiceData.taxRate,
            tax_amount:      invoiceData.taxAmount,
            discount_rate:   invoiceData.discountRate,
            discount_amount: invoiceData.discountAmount,
            total:           invoiceData.total,
            notes:           invoiceData.notes,
            payment_method:  invoiceData.paymentMethod,
            reference:       invoiceData.reference,
            status:          invoiceData.status,
          })
        });
        const data = await response.json();
        if (data.success) {
          console.log(`[InvoiceScreen] Saved to DB: ${invoiceData.invoiceNumber} for company ${cid}`);
          // Use server-assigned ID if available
          if (data.data?.id) invoiceData.id = data.data.id.toString();
        } else {
          console.warn('[InvoiceScreen] Save failed:', data.error);
        }
      } catch (error) {
        console.warn('Could not save to API:', error.message);
      }
    }
    
    setInvoices(prev => {
      const updated = [invoiceData, ...prev];
      const fixedUpdated = fixInvalidDates(updated);
      calculateInvoiceStatistics();
      return fixedUpdated;
    });

    // Add notification
    await addNotification({
      title: 'Invoice Created',
      message: `Invoice ${invoiceData.invoiceNumber} has been created successfully.`,
      type: 'success',
      priority: 'low'
    });
  };

  const updateInvoice = async (invoiceId, invoiceData) => {
    if (API_BASE_URL) {
      try {
        const headers = await getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/api/data/financial_invoices/${invoiceId}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify({
            client_name: invoiceData.clientName,
            client_email: invoiceData.clientEmail,
            client_company: invoiceData.clientCompany,
            client_address: invoiceData.clientAddress,
            client_vat: invoiceData.clientVAT,
            issue_date: invoiceData.issueDate.split('T')[0],
            due_date: invoiceData.dueDate.split('T')[0],
            paid_date: invoiceData.paidDate?.split('T')[0],
            description: invoiceData.description,
            items: invoiceData.items,
            subtotal: invoiceData.subtotal,
            tax_rate: invoiceData.taxRate,
            tax_amount: invoiceData.taxAmount,
            discount_rate: invoiceData.discountRate,
            discount_amount: invoiceData.discountAmount,
            total: invoiceData.total,
            notes: invoiceData.notes,
            payment_method: invoiceData.paymentMethod,
            reference: invoiceData.reference,
            status: invoiceData.status
          })
        });
        
        const data = await response.json();
        if (data.success) {
          console.log('Invoice updated in API');
        }
      } catch (error) {
        console.warn('Could not update in API:', error.message);
      }
    }
    
    setInvoices(prev => {
      const updated = prev.map(inv => 
        inv.id === invoiceId ? invoiceData : inv
      );
      const fixedUpdated = fixInvalidDates(updated);
      calculateInvoiceStatistics();
      return fixedUpdated;
    });
  };

  const deleteInvoice = async (invoiceId) => {
    if (API_BASE_URL) {
      try {
        const headers = await getAuthHeaders();
        await fetch(`${API_BASE_URL}/api/data/financial_invoices/${invoiceId}`, {
          method: 'DELETE',
          headers,
        });
        console.log(`[InvoiceScreen] Deleted invoice ${invoiceId} from API`);
      } catch (error) {
        console.warn('Could not delete from API:', error.message);
      }
    }
    
    setInvoices(prev => {
      const updated = prev.filter(inv => inv.id !== invoiceId);
      calculateInvoiceStatistics();
      return updated;
    });

    // Add notification
    await addNotification({
      title: 'Invoice Deleted',
      message: `Invoice has been deleted.`,
      type: 'warning',
      priority: 'low'
    });
  };

  // Navigate to CompanyProfileScreen
  const navigateToCompanyProfile = () => {
    navigation.navigate('CompanyProfileScreen', {
      companyProfile,
      companyProfile: {
        name: companyProfile.name,
        email: companyProfile.email,
        phone: companyProfile.phone,
        headquarters: companyProfile.headquarters,
        vatNumber: companyProfile.vatNumber,
        registrationNumber: companyProfile.registrationNumber,
        logo: companyProfile.logo,
        defaultTaxRate: companyProfile.defaultTaxRate,
        invoicePrefix: companyProfile.invoicePrefix,
        paymentTerms: companyProfile.paymentTerms,
        invoiceFooter: companyProfile.invoiceFooter,
        invoiceNotes: companyProfile.invoiceNotes,
        bankName: companyProfile.bankName,
        bankAccount: companyProfile.bankAccount,
        bankSortCode: companyProfile.bankSortCode,
        bankIBAN: companyProfile.bankIBAN,
        bankSWIFT: companyProfile.bankSWIFT,
      },
      onGoBack: async (updatedProfile) => {
        if (updatedProfile) {
          // Update local state
          setCompanyProfile(prev => ({
            ...prev,
            ...updatedProfile,
          }));
          
          // Update context
          await updateCompanyProfile(updatedProfile);
          
          // Update invoice form
          setInvoiceForm(prev => ({
            ...prev,
            taxRate: updatedProfile.defaultTaxRate || prev.taxRate || '20'
          }));

          await addNotification({
            title: 'Profile Updated',
            message: 'Company profile has been updated successfully.',
            type: 'success',
            priority: 'low'
          });
        }
      }
    });
  };

  // PDF Generation
  const generateInvoiceHTML = (invoice) => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Invoice ${invoice.invoiceNumber}</title>
          <style>
            body {
              font-family: 'Helvetica', Arial, sans-serif;
              margin: 0;
              padding: 20px;
              color: #333;
              line-height: 1.5;
            }
            .invoice-container {
              max-width: 800px;
              margin: 0 auto;
              background: white;
              border: 1px solid #e5e7eb;
              border-radius: 12px;
              padding: 30px;
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 30px;
              padding-bottom: 20px;
              border-bottom: 2px solid #0e4e96;
            }
            .company-info {
              display: flex;
              align-items: center;
            }
            .company-logo {
              width: 70px;
              height: 70px;
              margin-right: 15px;
              border-radius: 8px;
              object-fit: contain;
            }
            .company-details {
              flex: 1;
            }
            .company-name {
              font-size: 22px;
              font-weight: bold;
              color: #0e4e96;
              margin-bottom: 4px;
            }
            .company-address {
              font-size: 12px;
              color: #6b7280;
              line-height: 1.6;
              white-space: pre-line;
            }
            .invoice-title {
              text-align: right;
            }
            .invoice-number {
              font-size: 24px;
              color: #0e4e96;
              margin-bottom: 8px;
              font-weight: bold;
            }
            .status-badge {
              display: inline-block;
              padding: 6px 16px;
              border-radius: 20px;
              font-size: 12px;
              font-weight: 600;
              color: white;
              background-color: ${getStatusColor(invoice.status)};
              text-transform: capitalize;
            }
            .section {
              margin-bottom: 25px;
            }
            .section-title {
              font-size: 16px;
              font-weight: 600;
              color: #1f2937;
              margin-bottom: 12px;
              border-bottom: 1px solid #e5e7eb;
              padding-bottom: 8px;
            }
            .client-info {
              background: #f9fafb;
              padding: 16px;
              border-radius: 8px;
            }
            .client-info p {
              margin: 6px 0;
              color: #4b5563;
            }
            .client-vat {
              font-size: 12px;
              color: #0e4e96;
              margin-top: 8px;
              font-weight: 500;
            }
            .dates-grid {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              background: #f9fafb;
              padding: 16px;
              border-radius: 8px;
            }
            .date-item {
              flex: 1;
            }
            .date-label {
              font-size: 12px;
              color: #6b7280;
              margin-bottom: 4px;
            }
            .date-value {
              font-size: 15px;
              font-weight: 600;
              color: #1f2937;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
            }
            th {
              background: #f3f4f6;
              padding: 12px;
              text-align: left;
              font-size: 14px;
              font-weight: 600;
              color: #1f2937;
            }
            td {
              padding: 12px;
              border-bottom: 1px solid #e5e7eb;
              color: #4b5563;
            }
            .summary {
              margin-left: auto;
              width: 300px;
              background: #f9fafb;
              padding: 16px;
              border-radius: 8px;
            }
            .summary-row {
              display: flex;
              justify-content: space-between;
              padding: 8px 0;
              border-bottom: 1px solid #e5e7eb;
            }
            .total-row {
              font-size: 18px;
              font-weight: bold;
              color: #1f2937;
              border-top: 2px solid #0e4e96;
              padding-top: 12px;
              margin-top: 8px;
            }
            .total-amount {
              color: #0e4e96;
              font-size: 20px;
            }
            .bank-details {
              margin-top: 30px;
              padding: 16px;
              background: #f9fafb;
              border-radius: 8px;
              border-left: 4px solid #0e4e96;
            }
            .bank-title {
              font-size: 15px;
              font-weight: 600;
              color: #1f2937;
              margin-bottom: 12px;
            }
            .bank-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 12px;
            }
            .bank-item {
              font-size: 13px;
            }
            .bank-label {
              color: #6b7280;
              display: block;
              margin-bottom: 2px;
            }
            .bank-value {
              color: #1f2937;
              font-weight: 500;
            }
            .notes {
              margin-top: 20px;
              padding: 16px;
              background: #f9fafb;
              border-radius: 8px;
              font-size: 14px;
              color: #4b5563;
              font-style: italic;
            }
            .footer {
              margin-top: 30px;
              text-align: center;
              color: #9ca3af;
              font-size: 12px;
              border-top: 1px solid #e5e7eb;
              padding-top: 20px;
            }
            @media print {
              body { background: white; }
              .invoice-container { box-shadow: none; border: none; }
              .status-badge { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="invoice-container">
            <div class="header">
              <div class="company-info">
                ${companyProfile.logo ? `
                  <img src="${companyProfile.logo}" class="company-logo" alt="Company Logo" />
                ` : ''}
                <div class="company-details">
                  <div class="company-name">${companyProfile.name}</div>
                  <div class="company-address">
                    ${(companyProfile.headquarters || '').replace(/\n/g, '<br>')}
                  </div>
                  <div style="font-size: 11px; color: #6b7280; margin-top: 4px;">
                    VAT: ${companyProfile.vatNumber} | Reg: ${companyProfile.registrationNumber}
                  </div>
                </div>
              </div>
              <div class="invoice-title">
                <div class="invoice-number">${invoice.invoiceNumber}</div>
                <span class="status-badge">${invoice.status}</span>
              </div>
            </div>

            <div class="section">
              <div class="section-title">Bill To</div>
              <div class="client-info">
                <p style="font-weight: 600; color: #1f2937; font-size: 16px;">${invoice.clientName}</p>
                <p>${invoice.clientCompany}</p>
                <p>${invoice.clientEmail}</p>
                <p>${invoice.clientAddress || ''}</p>
                ${invoice.clientVAT ? `<p class="client-vat">VAT: ${invoice.clientVAT}</p>` : ''}
              </div>
            </div>

            <div class="dates-grid">
              <div class="date-item">
                <div class="date-label">Invoice Date</div>
                <div class="date-value">${formatDate(invoice.issueDate)}</div>
              </div>
              <div class="date-item">
                <div class="date-label">Due Date</div>
                <div class="date-value">${formatDate(invoice.dueDate)}</div>
              </div>
              ${invoice.paidDate ? `
                <div class="date-item">
                  <div class="date-label">Paid Date</div>
                  <div class="date-value">${formatDate(invoice.paidDate)}</div>
                </div>
              ` : ''}
            </div>

            <div class="section">
              <div class="section-title">Items</div>
              <table>
                <thead>
                  <tr>
                    <th>Description</th>
                    <th style="text-align: right;">Qty</th>
                    <th style="text-align: right;">Unit</th>
                    <th style="text-align: right;">Rate (£)</th>
                    <th style="text-align: right;">Amount (£)</th>
                  </tr>
                </thead>
                <tbody>
                  ${invoice.items.map(item => `
                    <tr>
                      <td>${item.description}</td>
                      <td style="text-align: right;">${item.quantity}</td>
                      <td style="text-align: right;">${item.unit || 'hr'}</td>
                      <td style="text-align: right;">${formatCurrency(item.rate)}</td>
                      <td style="text-align: right;">${formatCurrency(item.amount)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>

            <div class="summary">
              <div class="summary-row">
                <span>Subtotal</span>
                <span>${formatCurrency(invoice.subtotal)}</span>
              </div>
              ${invoice.discountAmount > 0 ? `
                <div class="summary-row">
                  <span>Discount (${invoice.discountRate}%)</span>
                  <span>-${formatCurrency(invoice.discountAmount)}</span>
                </div>
              ` : ''}
              <div class="summary-row">
                <span>VAT (${invoice.taxRate}%)</span>
                <span>${formatCurrency(invoice.taxAmount)}</span>
              </div>
              <div class="summary-row total-row">
                <span>Total</span>
                <span class="total-amount">${formatCurrency(invoice.total)}</span>
              </div>
            </div>

            <div class="bank-details">
              <div class="bank-title">Payment Information</div>
              <div class="bank-grid">
                <div class="bank-item">
                  <span class="bank-label">Bank:</span>
                  <span class="bank-value">${companyProfile.bankName}</span>
                </div>
                <div class="bank-item">
                  <span class="bank-label">Account:</span>
                  <span class="bank-value">${companyProfile.bankAccount}</span>
                </div>
                <div class="bank-item">
                  <span class="bank-label">Sort Code:</span>
                  <span class="bank-value">${companyProfile.bankSortCode}</span>
                </div>
                <div class="bank-item">
                  <span class="bank-label">IBAN:</span>
                  <span class="bank-value">${companyProfile.bankIBAN}</span>
                </div>
                <div class="bank-item">
                  <span class="bank-label">SWIFT:</span>
                  <span class="bank-value">${companyProfile.bankSWIFT}</span>
                </div>
              </div>
            </div>

            ${invoice.notes ? `
              <div class="notes">
                <strong style="color: #1f2937;">Notes</strong>
                <p style="margin-top: 8px; margin-bottom: 0;">${invoice.notes}</p>
              </div>
            ` : ''}

            <div class="footer">
              <p>${companyProfile.invoiceFooter || 'Thank you for your business.'}</p>
              <p style="margin-top: 8px;">${companyProfile.name} | VAT: ${companyProfile.vatNumber}</p>
              <p style="margin-top: 8px;">Generated on ${new Date().toLocaleString('en-GB', { 
                day: 'numeric', 
                month: 'long', 
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}</p>
            </div>
          </div>
        </body>
      </html>
    `;
  };

  const generateAndPrintPDF = async (invoice) => {
    try {
      const html = generateInvoiceHTML(invoice);
      
      if (Platform.OS === 'web') {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(html);
        printWindow.document.close();
        printWindow.focus();
        printWindow.print();
      } else {
        const { uri } = await Print.printToFileAsync({ html });
        
        if (await Sharing.isAvailableAsync()) {
          await Sharing.shareAsync(uri, {
            mimeType: 'application/pdf',
            dialogTitle: `Share Invoice ${invoice.invoiceNumber}`,
            UTI: 'com.adobe.pdf'
          });
        } else {
          await Print.printAsync({ html });
        }
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      Alert.alert('Error', 'Failed to generate PDF. Please try again.');
    }
  };

  const shareInvoiceLink = async (invoice) => {
    try {
      await Share.share({
        message: `Invoice ${invoice.invoiceNumber} from ${companyProfile.name}\nAmount: ${formatCurrency(invoice.total)}\nDue Date: ${formatDate(invoice.dueDate)}`,
        title: `Invoice ${invoice.invoiceNumber}`,
      });
    } catch (error) {
      console.error('Error sharing invoice:', error);
      Alert.alert('Error', 'Failed to share invoice');
    }
  };

  // OCR Processing
  const requestCameraPermissions = async () => {
    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      Alert.alert('Permission Required', 'Camera permission is required to scan invoices');
      return false;
    }
    return true;
  };

  const processInvoiceOCR = async (imageUri) => {
    setProcessingOCR(true);
    try {
      const optimizedImage = await manipulateAsync(
        imageUri,
        [{ resize: { width: 1200 } }],
        { compress: 0.8, format: SaveFormat.JPEG }
      );

      await FileSystem.readAsStringAsync(optimizedImage.uri, {
        encoding: FileSystem.EncodingType.Base64,
      });

      setTimeout(() => {
        const now = new Date();
        const prefix = companyProfile.invoicePrefix || 'INV';
        const taxRate = parseFloat(companyProfile.defaultTaxRate) || 20;
        const paymentTerms = parseInt(companyProfile.paymentTerms || 30);
        
        const dueDate = new Date(now.getTime() + paymentTerms * 24 * 60 * 60 * 1000);
        
        const mockExtractedData = {
          client_name: 'ABC Company Ltd',
          client_email: 'accounts@abccompany.com',
          client_company: 'ABC Company Ltd',
          client_address: '123 Business Park, London, UK',
          client_vat: 'GB123456789',
          invoice_number: `${prefix}-${now.getFullYear()}-${String(invoices.length + 1).padStart(3, '0')}`,
          issue_date: now.toISOString().split('T')[0],
          due_date: dueDate.toISOString().split('T')[0],
          description: 'Professional Services',
          items: [
            { description: 'Consulting Services', quantity: 10, rate: 150, amount: 1500, unit: 'hour' },
            { description: 'Development Services', quantity: 20, rate: 120, amount: 2400, unit: 'hour' }
          ],
          subtotal: 3900,
          tax_rate: taxRate,
          tax_amount: 3900 * (taxRate / 100),
          total_amount: 3900 + (3900 * (taxRate / 100)),
          notes: 'OCR extracted invoice'
        };

        const ocrInvoiceData = {
          clientName: mockExtractedData.client_name || '',
          clientEmail: mockExtractedData.client_email || '',
          clientCompany: mockExtractedData.client_company || '',
          clientAddress: mockExtractedData.client_address || '',
          clientVAT: mockExtractedData.client_vat || '',
          invoiceNumber: mockExtractedData.invoice_number || generateInvoiceNumber(),
          issueDate: mockExtractedData.issue_date || now.toISOString().split('T')[0],
          dueDate: mockExtractedData.due_date || dueDate.toISOString().split('T')[0],
          description: mockExtractedData.description || `Invoice ${mockExtractedData.invoice_number || ''}`,
          items: mockExtractedData.items && mockExtractedData.items.length > 0 
            ? mockExtractedData.items.map((item, index) => ({
                id: String(index + 1),
                description: item.description || 'Item',
                quantity: parseFloat(item.quantity) || 1,
                rate: parseFloat(item.rate) || 0,
                amount: parseFloat(item.amount) || 0,
                unit: item.unit || 'hr'
              }))
            : [{ id: '1', description: 'Services', quantity: 1, rate: mockExtractedData.total_amount || 0, amount: mockExtractedData.total_amount || 0, unit: 'hr' }],
          subtotal: parseFloat(mockExtractedData.subtotal) || 0,
          taxRate: mockExtractedData.tax_rate ? parseFloat(mockExtractedData.tax_rate).toString() : companyProfile.defaultTaxRate || '20',
          taxAmount: parseFloat(mockExtractedData.tax_amount) || 0,
          discountRate: '0',
          discountAmount: 0,
          total: parseFloat(mockExtractedData.total_amount) || 0,
          notes: mockExtractedData.notes || '',
        };

        setOcrResult(ocrInvoiceData);
        
        setInvoiceForm({
          ...invoiceForm,
          ...ocrInvoiceData,
          invoiceNumber: ocrInvoiceData.invoiceNumber || generateInvoiceNumber(),
          issueDate: ocrInvoiceData.issueDate,
          dueDate: ocrInvoiceData.dueDate,
          taxRate: ocrInvoiceData.taxRate,
        });

        Alert.alert(
          'OCR Complete',
          'Invoice data extracted successfully. Please review and complete the information.',
          [{ text: 'OK' }]
        );
        
        setShowOCRSModal(false);
        setShowCreateModal(true);
        setProcessingOCR(false);
      }, 2000);
      
    } catch (error) {
      console.error('OCR processing error:', error);
      Alert.alert('OCR Error', 'Failed to process invoice image. Please try again or enter details manually.');
      setProcessingOCR(false);
    }
  };

  const pickImageForOCR = async () => {
    const hasPermission = await requestCameraPermissions();
    if (!hasPermission) return;

    Alert.alert(
      'Scan Invoice',
      'Choose an option to scan your invoice',
      [
        {
          text: 'Take Photo',
          onPress: async () => {
            try {
              const result = await ImagePicker.launchCameraAsync({
                mediaTypes: ['images'],
                allowsEditing: true,
                quality: 0.8,
              });

              if (!result.canceled && result.assets && result.assets[0]) {
                await processInvoiceOCR(result.assets[0].uri);
              }
            } catch (error) {
              console.error('Error taking photo:', error);
              Alert.alert('Error', 'Failed to take photo');
            }
          },
        },
        {
          text: 'Choose from Gallery',
          onPress: async () => {
            try {
              const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ['images'],
                allowsEditing: true,
                quality: 0.8,
              });

              if (!result.canceled && result.assets && result.assets[0]) {
                await processInvoiceOCR(result.assets[0].uri);
              }
            } catch (error) {
              console.error('Error picking image:', error);
              Alert.alert('Error', 'Failed to pick image');
            }
          },
        },
        { text: 'Cancel', style: 'cancel' },
      ]
    );
  };

  // Invoice Management Functions
  const generateInvoiceNumber = () => {
    const year = new Date().getFullYear();
    const prefix = companyProfile.invoicePrefix || 'INV';
    const nextNumber = invoices.length + 1;
    return `${prefix}-${year}-${String(nextNumber).padStart(3, '0')}`;
  };

  const calculateInvoiceTotals = (items, taxRate, discountRate) => {
    const subtotal = items.reduce((sum, item) => sum + (item.quantity * item.rate), 0);
    const discountAmount = (subtotal * (discountRate || 0)) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * (taxRate || 0)) / 100;
    const total = taxableAmount + taxAmount;
    
    return {
      subtotal,
      discountAmount,
      taxAmount,
      total
    };
  };

  const handleCreateInvoice = () => {
    const now = new Date();
    const dueDate = new Date(now.getTime() + parseInt(companyProfile.paymentTerms || 30) * 24 * 60 * 60 * 1000);
    
    setInvoiceForm({
      clientName: '',
      clientEmail: '',
      clientCompany: '',
      clientAddress: '',
      clientVAT: '',
      invoiceNumber: generateInvoiceNumber(),
      issueDate: now.toISOString().split('T')[0],
      dueDate: dueDate.toISOString().split('T')[0],
      description: '',
      items: [{ id: '1', description: '', quantity: 1, rate: 0, amount: 0, unit: 'hr' }],
      notes: companyProfile.invoiceNotes || '',
      taxRate: companyProfile.defaultTaxRate || '20',
      discountRate: '0',
      reference: '',
      paymentMethod: 'Bank Transfer'
    });
    setOcrResult(null);
    setShowCreateModal(true);
  };

  const handleSaveInvoice = async () => {
    if (!invoiceForm.clientName || !invoiceForm.clientCompany || !invoiceForm.description) {
      Alert.alert('Error', 'Please fill in all required fields');
      return;
    }

    if (invoiceForm.items.length === 0 || invoiceForm.items.some(item => !item.description || item.rate <= 0)) {
      Alert.alert('Error', 'Please add at least one valid invoice item');
      return;
    }

    const totals = calculateInvoiceTotals(
      invoiceForm.items,
      parseFloat(invoiceForm.taxRate) || 0,
      parseFloat(invoiceForm.discountRate) || 0
    );

    const newInvoice = {
      id: Date.now().toString(),
      invoiceNumber: invoiceForm.invoiceNumber,
      clientName: invoiceForm.clientName,
      clientEmail: invoiceForm.clientEmail,
      clientCompany: invoiceForm.clientCompany,
      clientAddress: invoiceForm.clientAddress,
      clientVAT: invoiceForm.clientVAT,
      issueDate: new Date(invoiceForm.issueDate).toISOString(),
      dueDate: new Date(invoiceForm.dueDate).toISOString(),
      paidDate: null,
      status: 'draft',
      description: invoiceForm.description,
      items: invoiceForm.items.map(item => ({
        ...item,
        amount: item.quantity * item.rate
      })),
      subtotal: totals.subtotal,
      taxRate: parseFloat(invoiceForm.taxRate) || 0,
      taxAmount: totals.taxAmount,
      discountRate: parseFloat(invoiceForm.discountRate) || 0,
      discountAmount: totals.discountAmount,
      total: totals.total,
      notes: invoiceForm.notes || companyProfile.invoiceFooter,
      paymentMethod: invoiceForm.paymentMethod || 'Bank Transfer',
      reference: invoiceForm.reference || ''
    };

    await saveInvoice(newInvoice);
    setShowCreateModal(false);
    Alert.alert('Success', 'Invoice created successfully');
  };

  const handleSendInvoice = async (invoiceId) => {
    const updatedInvoice = invoices.find(inv => inv.id === invoiceId);
    if (updatedInvoice) {
      updatedInvoice.status = 'sent';
      await updateInvoice(invoiceId, updatedInvoice);
      Alert.alert('Success', 'Invoice sent successfully');
      
      await addNotification({
        title: 'Invoice Sent',
        message: `Invoice ${updatedInvoice.invoiceNumber} has been sent.`,
        type: 'info',
        priority: 'medium'
      });
    }
  };

  const handleMarkAsPaid = (invoiceId) => {
    Alert.alert(
      'Mark as Paid',
      'Are you sure you want to mark this invoice as paid?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Mark Paid',
          onPress: async () => {
            const updatedInvoice = invoices.find(inv => inv.id === invoiceId);
            if (updatedInvoice) {
              updatedInvoice.status = 'paid';
              updatedInvoice.paidDate = new Date().toISOString();
              await updateInvoice(invoiceId, updatedInvoice);
              Alert.alert('Success', 'Invoice marked as paid');
              
              await addNotification({
                title: 'Payment Received',
                message: `Payment for invoice ${updatedInvoice.invoiceNumber} has been received.`,
                type: 'success',
                priority: 'high'
              });
            }
          }
        }
      ]
    );
  };

  const handleDeleteInvoice = (invoiceId) => {
    Alert.alert(
      'Delete Invoice',
      'Are you sure you want to delete this invoice? This action cannot be undone.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            await deleteInvoice(invoiceId);
          }
        }
      ]
    );
  };

  const addInvoiceItem = () => {
    const newItem = {
      id: String(invoiceForm.items.length + 1),
      description: '',
      quantity: 1,
      rate: 0,
      amount: 0,
      unit: 'hr'
    };
    setInvoiceForm({
      ...invoiceForm,
      items: [...invoiceForm.items, newItem]
    });
  };

  const updateInvoiceItem = (itemId, field, value) => {
    const updatedItems = invoiceForm.items.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: field === 'quantity' || field === 'rate' ? parseFloat(value) || 0 : value };
        if (field === 'quantity' || field === 'rate') {
          updatedItem.amount = updatedItem.quantity * updatedItem.rate;
        }
        return updatedItem;
      }
      return item;
    });
    setInvoiceForm({ ...invoiceForm, items: updatedItems });
  };

  const removeInvoiceItem = (itemId) => {
    setInvoiceForm({
      ...invoiceForm,
      items: invoiceForm.items.filter(item => item.id !== itemId)
    });
  };

  // Utility Functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': '#6B7280',
      'sent': '#3B82F6',
      'pending': '#F59E0B',
      'paid': '#10B981',
      'overdue': '#EF4444',
      'cancelled': '#6B7280'
    };
    return colors[status] || '#6B7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'draft': 'file-edit',
      'sent': 'send',
      'pending': 'clock',
      'paid': 'check-circle',
      'overdue': 'alert-circle',
      'cancelled': 'close-circle'
    };
    return icons[status] || 'file';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Professional Services': 'account-tie',
      'IT & Development': 'code-braces',
      'Creative Services': 'palette',
      'Marketing': 'bullhorn',
      'Products': 'package-variant',
      'Services': 'wrench',
      'Construction': 'hammer',
      'Engineering': 'engine',
      'Other': 'file-document'
    };
    return icons[category] || 'file-document';
  };

  const filteredInvoices = invoices.filter(invoice => {
    const matchesSearch = invoice.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.clientName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.clientCompany?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         invoice.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || invoice.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  // Group templates by category
  const groupedTemplates = invoiceTemplates.reduce((groups, template) => {
    const category = template.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(template);
    return groups;
  }, {});

  // Render Functions
  const renderHeader = () => (
    <LinearGradient
      colors={['#0e4e96', '#2670ba']}
      style={styles.header}
    >
      <View style={styles.headerContent}>
        <TouchableOpacity 
          onPress={() => navigation?.goBack()}
          style={styles.backButton}
        >
          <MaterialCommunityIcons name="arrow-left" size={24} color="#FFFFFF" />
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={styles.headerCenter}
          onPress={navigateToCompanyProfile}
        >
          <View style={styles.headerCompanyInfo}>
            {companyProfile.logo ? (
              <Image 
                source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} 
                style={styles.headerLogo}
              />
            ) : (
              <View style={styles.headerLogoPlaceholder}>
                <MaterialCommunityIcons name="office-building" size={20} color="#FFFFFF" />
              </View>
            )}
            <View style={styles.headerCompanyDetails}>
              <Text style={styles.headerTitle}>{companyProfile.name}</Text>
              <Text style={styles.headerSubtitle}>Tap to edit profile</Text>
            </View>
          </View>
        </TouchableOpacity>
        
        <View style={styles.headerButtons}>
          <TouchableOpacity 
            style={[styles.headerButton, styles.headerButtonLeft]}
            onPress={() => navigation?.navigate?.('FinancialManagementScreen', {
              companyProfile, companyId: companyProfile.id,
            })}
          >
            <MaterialCommunityIcons name="chart-line" size={22} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.headerButton, styles.headerButtonLeft]}
            onPress={() => setShowOCRSModal(true)}
          >
            <MaterialCommunityIcons name="camera" size={22} color="#FFFFFF" />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.headerButton}
            onPress={handleCreateInvoice}
          >
            <MaterialCommunityIcons name="plus" size={22} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      </View>
      
      {syncStatus === 'syncing' && (
        <View style={styles.syncIndicator}>
          <ActivityIndicator size="small" color="#FFFFFF" />
          <Text style={styles.syncText}>Syncing...</Text>
        </View>
      )}
      
      {!isOnline && (
        <View style={styles.offlineIndicator}>
          <MaterialCommunityIcons name="cloud-offline" size={16} color="#FFFFFF" />
          <Text style={styles.offlineText}>Offline Mode</Text>
        </View>
      )}
    </LinearGradient>
  );

  const renderTabs = () => (
    <View style={styles.tabsContainer}>
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.tabsContent}
      >
        {[
          { key: 'invoices', title: 'Invoices', icon: 'receipt' },
          { key: 'templates', title: 'Templates', icon: 'file-multiple' },
          { key: 'payments', title: 'Payments', icon: 'credit-card' },
          { key: 'analytics', title: 'Analytics', icon: 'chart-line' }
        ].map((tab) => (
          <TouchableOpacity
            key={tab.key}
            style={[
              styles.tab,
              activeTab === tab.key && styles.activeTab
            ]}
            onPress={() => setActiveTab(tab.key)}
          >
            <MaterialCommunityIcons 
              name={tab.icon} 
              size={20} 
              color={activeTab === tab.key ? '#0e4e96' : '#6B7280'} 
            />
            <Text style={[
              styles.tabText,
              activeTab === tab.key && styles.activeTabText
            ]}>
              {tab.title}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderSearchAndFilter = () => (
    <View style={styles.searchContainer}>
      <View style={styles.searchBar}>
        <MaterialCommunityIcons name="magnify" size={20} color="#6B7280" />
        <TextInput
          style={styles.searchInput}
          placeholder="Search invoices..."
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholderTextColor="#9CA3AF"
        />
        {searchQuery.length > 0 && (
          <TouchableOpacity onPress={() => setSearchQuery('')}>
            <MaterialCommunityIcons name="close" size={20} color="#6B7280" />
          </TouchableOpacity>
        )}
      </View>
      
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={styles.filterContainer}
      >
        {[
          { key: 'all', label: 'All' },
          { key: 'draft', label: 'Draft' },
          { key: 'sent', label: 'Sent' },
          { key: 'pending', label: 'Pending' },
          { key: 'paid', label: 'Paid' },
          { key: 'overdue', label: 'Overdue' }
        ].map((filter) => (
          <TouchableOpacity
            key={filter.key}
            style={[
              styles.filterButton,
              filterStatus === filter.key && styles.activeFilterButton
            ]}
            onPress={() => setFilterStatus(filter.key)}
          >
            <Text style={[
              styles.filterButtonText,
              filterStatus === filter.key && styles.activeFilterButtonText
            ]}>
              {filter.label}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderInvoiceStats = () => {
    const stats = [
      {
        title: 'Total Invoices',
        value: invoiceStats.totalInvoices.toString(),
        icon: 'receipt',
        color: '#0e4e96'
      },
      {
        title: 'Paid',
        value: invoiceStats.paidInvoices.toString(),
        icon: 'check-circle',
        color: '#10B981'
      },
      {
        title: 'Total Value',
        value: formatCurrency(invoiceStats.totalValue),
        icon: 'currency-usd',
        color: '#F59E0B'
      },
      {
        title: 'Avg Payment',
        value: `${invoiceStats.avgPaymentTime} days`,
        icon: 'clock',
        color: '#8B5CF6'
      }
    ];

    return (
      <FlatList
        data={stats}
        keyExtractor={(item, index) => index.toString()}
        renderItem={({ item }) => (
          <View style={styles.statCard}>
            <View style={[styles.statIcon, { backgroundColor: item.color }]}>
              <MaterialCommunityIcons name={item.icon} size={24} color="#FFFFFF" />
            </View>
            <Text style={styles.statValue}>{item.value}</Text>
            <Text style={styles.statTitle}>{item.title}</Text>
          </View>
        )}
        numColumns={2}
        columnWrapperStyle={styles.row}
        contentContainerStyle={styles.statsContainer}
        scrollEnabled={false}
      />
    );
  };

  const renderInvoicesList = () => (
    <FlatList
      data={filteredInvoices}
      keyExtractor={(item) => item.id}
      renderItem={({ item }) => (
        <TouchableOpacity 
          style={styles.invoiceCard}
          onPress={() => {
            setSelectedInvoice(item);
            setShowInvoiceModal(true);
          }}
        >
          <View style={styles.invoiceHeader}>
            <View style={styles.invoiceInfo}>
              <Text style={styles.invoiceNumber}>{item.invoiceNumber}</Text>
              <Text style={styles.invoiceClient}>{item.clientName} - {item.clientCompany}</Text>
              <Text style={styles.invoiceDescription}>{item.description}</Text>
            </View>
            <View style={styles.invoiceStatus}>
              <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) }]}>
                <MaterialCommunityIcons 
                  name={getStatusIcon(item.status)} 
                  size={12} 
                  color="#FFFFFF" 
                />
                <Text style={styles.statusText}>{item.status}</Text>
              </View>
            </View>
          </View>
          
          <View style={styles.invoiceDetails}>
            <View style={styles.invoiceDetail}>
              <Text style={styles.invoiceDetailLabel}>Amount</Text>
              <Text style={styles.invoiceDetailValue}>{formatCurrency(item.total)}</Text>
            </View>
            <View style={styles.invoiceDetail}>
              <Text style={styles.invoiceDetailLabel}>Issue Date</Text>
              <Text style={styles.invoiceDetailValue}>{formatDate(item.issueDate)}</Text>
            </View>
            <View style={styles.invoiceDetail}>
              <Text style={styles.invoiceDetailLabel}>Due Date</Text>
              <Text style={styles.invoiceDetailValue}>{formatDate(item.dueDate)}</Text>
            </View>
          </View>
          
          <View style={styles.invoiceActions}>
            <TouchableOpacity 
              style={[styles.actionButton, { backgroundColor: '#0e4e96' }]}
              onPress={() => generateAndPrintPDF(item)}
            >
              <MaterialCommunityIcons name="file-pdf-box" size={16} color="#FFFFFF" />
              <Text style={styles.actionButtonText}>PDF</Text>
            </TouchableOpacity>
            
            {item.status === 'draft' && (
              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: '#0e4e96' }]}
                onPress={() => handleSendInvoice(item.id)}
              >
                <MaterialCommunityIcons name="send" size={16} color="#FFFFFF" />
                <Text style={styles.actionButtonText}>Send</Text>
              </TouchableOpacity>
            )}
            {(item.status === 'sent' || item.status === 'pending' || item.status === 'overdue') && (
              <TouchableOpacity 
                style={[styles.actionButton, { backgroundColor: '#10B981' }]}
                onPress={() => handleMarkAsPaid(item.id)}
              >
                <MaterialCommunityIcons name="check" size={16} color="#FFFFFF" />
                <Text style={styles.actionButtonText}>Mark Paid</Text>
              </TouchableOpacity>
            )}
            <TouchableOpacity 
              style={[styles.actionButton, { backgroundColor: '#EF4444' }]}
              onPress={() => handleDeleteInvoice(item.id)}
            >
              <MaterialCommunityIcons name="delete" size={16} color="#FFFFFF" />
              <Text style={styles.actionButtonText}>Delete</Text>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      )}
      ListEmptyComponent={() => (
        <View style={styles.emptyState}>
          <MaterialCommunityIcons name="receipt" size={64} color="#D1D5DB" />
          <Text style={styles.emptyStateTitle}>No Invoices Found</Text>
          <Text style={styles.emptyStateText}>
            {searchQuery || filterStatus !== 'all' 
              ? 'Try adjusting your search or filters'
              : 'Create your first invoice to get started'}
          </Text>
        </View>
      )}
      contentContainerStyle={styles.listContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#0e4e96']}
        />
      }
      showsVerticalScrollIndicator={false}
    />
  );

  const renderTemplatesList = () => (
    <FlatList
      data={Object.entries(groupedTemplates)}
      keyExtractor={(item) => item[0]}
      renderItem={({ item: [category, templates] }) => (
        <View style={styles.categorySection}>
          <View style={styles.categoryHeader}>
            <MaterialCommunityIcons 
              name={getCategoryIcon(category)} 
              size={20} 
              color="#0e4e96" 
            />
            <Text style={styles.categoryTitle}>{category}</Text>
            <Text style={styles.categoryCount}>{templates.length} templates</Text>
          </View>
          <FlatList
            data={templates}
            keyExtractor={(template) => template.id}
            horizontal
            showsHorizontalScrollIndicator={false}
            renderItem={({ item: template }) => (
              <TouchableOpacity 
                style={styles.templateCard}
                onPress={() => {
                  const now = new Date();
                  const paymentTerms = parseInt(template.paymentTerms) || 30;
                  const dueDate = new Date(now.getTime() + paymentTerms * 24 * 60 * 60 * 1000);
                  
                  setInvoiceForm({
                    ...invoiceForm,
                    invoiceNumber: generateInvoiceNumber(),
                    items: template.items.map((templateItem, index) => ({
                      id: String(index + 1),
                      description: templateItem.description,
                      quantity: templateItem.quantity || 1,
                      rate: templateItem.rate || 0,
                      amount: (templateItem.quantity || 1) * (templateItem.rate || 0),
                      unit: templateItem.unit || 'hr'
                    })),
                    taxRate: (template.taxRate || 20).toString(),
                    discountRate: (template.discountRate || 0).toString(),
                    notes: template.notes || '',
                    dueDate: dueDate.toISOString().split('T')[0]
                  });
                  setShowCreateModal(true);
                }}
              >
                <View style={styles.templateIconContainer}>
                  <MaterialCommunityIcons 
                    name={template.icon || getCategoryIcon(category)} 
                    size={24} 
                    color="#0e4e96" 
                  />
                </View>
                <View style={styles.templateHeader}>
                  <Text style={styles.templateName}>{template.name}</Text>
                </View>
                <Text style={styles.templateDescription}>{template.description}</Text>
                <View style={styles.templateItems}>
                  <Text style={styles.templateItemsTitle}>Typical items:</Text>
                  {template.items.slice(0, 3).map((templateItem, index) => (
                    <Text key={index} style={styles.templateItem}>
                      • {templateItem.description} ({templateItem.quantity} {templateItem.unit})
                    </Text>
                  ))}
                  {template.items.length > 3 && (
                    <Text style={styles.templateItem}>• +{template.items.length - 3} more items</Text>
                  )}
                </View>
                <View style={styles.templateFooter}>
                  <Text style={styles.templateTax}>VAT: {template.taxRate}%</Text>
                  {template.discountRate > 0 && (
                    <Text style={styles.templateDiscount}>Discount: {template.discountRate}%</Text>
                  )}
                  <Text style={styles.templatePayment}>Terms: {template.paymentTerms}</Text>
                </View>
              </TouchableOpacity>
            )}
            contentContainerStyle={styles.templatesRow}
          />
        </View>
      )}
      contentContainerStyle={styles.templatesListContainer}
      showsVerticalScrollIndicator={false}
    />
  );

  const renderPayments = () => {
    const paidInvoices = invoices.filter(invoice => invoice.status === 'paid');
    const pendingPayments = invoices.filter(invoice => 
      invoice.status === 'sent' || invoice.status === 'pending' || invoice.status === 'overdue'
    );

    return (
      <ScrollView 
        style={styles.contentContainer}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#0e4e96']}
          />
        }
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.paymentsSection}>
          <Text style={styles.sectionTitle}>Recent Payments</Text>
          {paidInvoices.length > 0 ? (
            paidInvoices.slice(0, 5).map((invoice) => (
              <View key={invoice.id} style={styles.paymentCard}>
                <View style={styles.paymentHeader}>
                  <Text style={styles.paymentInvoice}>{invoice.invoiceNumber}</Text>
                  <Text style={styles.paymentAmount}>{formatCurrency(invoice.total)}</Text>
                </View>
                <Text style={styles.paymentClient}>{invoice.clientCompany}</Text>
                <Text style={styles.paymentDate}>Paid on {formatDate(invoice.paidDate)}</Text>
                <Text style={styles.paymentMethod}>via {invoice.paymentMethod}</Text>
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>No paid invoices yet</Text>
          )}
        </View>
        
        <View style={styles.paymentsSection}>
          <Text style={styles.sectionTitle}>Pending Payments</Text>
          {pendingPayments.length > 0 ? (
            pendingPayments.slice(0, 5).map((invoice) => (
              <View key={invoice.id} style={styles.pendingCard}>
                <View style={styles.pendingHeader}>
                  <Text style={styles.pendingInvoice}>{invoice.invoiceNumber}</Text>
                  <Text style={styles.pendingAmount}>{formatCurrency(invoice.total)}</Text>
                </View>
                <Text style={styles.pendingClient}>{invoice.clientCompany}</Text>
                <Text style={styles.pendingDue}>Due: {formatDate(invoice.dueDate)}</Text>
                <View style={[styles.pendingStatus, { backgroundColor: getStatusColor(invoice.status) }]}>
                  <Text style={styles.pendingStatusText}>{invoice.status}</Text>
                </View>
              </View>
            ))
          ) : (
            <Text style={styles.emptyText}>No pending payments</Text>
          )}
        </View>
      </ScrollView>
    );
  };

  const renderAnalytics = () => (
    <ScrollView 
      style={styles.contentContainer}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          colors={['#0e4e96']}
        />
      }
      showsVerticalScrollIndicator={false}
    >
      {renderInvoiceStats()}
      
      <View style={styles.analyticsSection}>
        <Text style={styles.sectionTitle}>Invoice Status Distribution</Text>
        <View style={styles.statusDistribution}>
          {[
            { status: 'paid', count: invoiceStats.paidInvoices, color: '#10B981' },
            { status: 'pending', count: invoiceStats.pendingInvoices, color: '#F59E0B' },
            { status: 'overdue', count: invoiceStats.overdueInvoices, color: '#EF4444' },
            { status: 'draft', count: invoiceStats.draftInvoices, color: '#6B7280' },
            { status: 'sent', count: invoiceStats.sentInvoices, color: '#0e4e96' }
          ].map((item, index) => (
            <View key={index} style={styles.statusItem}>
              <View style={[styles.statusIndicator, { backgroundColor: item.color }]} />
              <Text style={styles.statusLabel}>{item.status}</Text>
              <Text style={styles.statusCount}>{item.count}</Text>
            </View>
          ))}
        </View>
      </View>
      
      <View style={styles.analyticsSection}>
        <Text style={styles.sectionTitle}>Revenue Breakdown</Text>
        <View style={styles.revenueBreakdown}>
          <View style={styles.revenueItem}>
            <Text style={styles.revenueLabel}>Paid Invoices</Text>
            <Text style={styles.revenueAmount}>{formatCurrency(invoiceStats.paidValue)}</Text>
          </View>
          <View style={styles.revenueItem}>
            <Text style={styles.revenueLabel}>Pending Payments</Text>
            <Text style={styles.revenueAmount}>{formatCurrency(invoiceStats.pendingValue)}</Text>
          </View>
          <View style={styles.revenueItem}>
            <Text style={styles.revenueLabel}>Overdue Amount</Text>
            <Text style={styles.revenueAmount}>{formatCurrency(invoiceStats.overdueValue)}</Text>
          </View>
          <View style={styles.revenueItem}>
            <Text style={styles.revenueLabel}>Average Invoice</Text>
            <Text style={styles.revenueAmount}>{formatCurrency(invoiceStats.avgInvoiceValue)}</Text>
          </View>
        </View>
      </View>
    </ScrollView>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'invoices':
        return (
          <View style={styles.contentContainer}>
            {renderSearchAndFilter()}
            {renderInvoicesList()}
          </View>
        );
      case 'templates':
        return renderTemplatesList();
      case 'payments':
        return renderPayments();
      case 'analytics':
        return renderAnalytics();
      default:
        return null;
    }
  };

  // OCR Scanner Modal
  const renderOCRSModal = () => (
    <Modal
      visible={showOCRSModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowOCRSModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Scan Invoice with OCR</Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => setShowOCRSModal(false)}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        <ScrollView style={styles.modalContent}>
          <View style={styles.ocrContainer}>
            <View style={styles.ocrIllustration}>
              <MaterialCommunityIcons name="camera" size={80} color="#0e4e96" />
            </View>
            
            <Text style={styles.ocrTitle}>Smart Invoice Scanner</Text>
            <Text style={styles.ocrDescription}>
              Take a photo or upload an invoice image. Our OCR technology will automatically extract:
            </Text>
            
            <View style={styles.ocrFeatures}>
              <View style={styles.ocrFeature}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
                <Text style={styles.ocrFeatureText}>Client information</Text>
              </View>
              <View style={styles.ocrFeature}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
                <Text style={styles.ocrFeatureText}>Invoice number & dates</Text>
              </View>
              <View style={styles.ocrFeature}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
                <Text style={styles.ocrFeatureText}>Line items & quantities</Text>
              </View>
              <View style={styles.ocrFeature}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
                <Text style={styles.ocrFeatureText}>Tax & discount information</Text>
              </View>
              <View style={styles.ocrFeature}>
                <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
                <Text style={styles.ocrFeatureText}>Total amount</Text>
              </View>
            </View>
            
            {processingOCR && (
              <View style={styles.ocrProcessing}>
                <ActivityIndicator size={36} color="#0e4e96" />
                <Text style={styles.ocrProcessingText}>Processing invoice...</Text>
                <Text style={styles.ocrProcessingSubtext}>This may take a few seconds</Text>
              </View>
            )}
          </View>
        </ScrollView>
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.cancelButton]}
            onPress={() => setShowOCRSModal(false)}
            disabled={processingOCR}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.modalButton, styles.cameraButton]}
            onPress={pickImageForOCR}
            disabled={processingOCR}
          >
            <MaterialCommunityIcons name="camera" size={20} color="#FFFFFF" />
            <Text style={styles.cameraButtonText}>Scan Invoice</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const renderCreateModal = () => (
    <Modal
      visible={showCreateModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => setShowCreateModal(false)}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>Create New Invoice</Text>
          <View style={styles.modalHeaderButtons}>
            <TouchableOpacity 
              style={[styles.modalHeaderButton, styles.ocrHeaderButton]}
              onPress={() => {
                setShowCreateModal(false);
                setShowOCRSModal(true);
              }}
            >
              <MaterialCommunityIcons name="camera" size={20} color="#0e4e96" />
            </TouchableOpacity>
            <TouchableOpacity 
              style={styles.modalCloseButton}
              onPress={() => setShowCreateModal(false)}
            >
              <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
            </TouchableOpacity>
          </View>
        </View>
        
        {ocrResult && (
          <View style={styles.ocrSuccessBanner}>
            <MaterialCommunityIcons name="check-circle" size={20} color="#10B981" />
            <Text style={styles.ocrSuccessText}>Invoice data extracted successfully</Text>
          </View>
        )}
        
        <ScrollView style={styles.modalContent}>
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Invoice Information</Text>
            <TextInput
              style={[styles.formInput, styles.disabledInput]}
              placeholder="Invoice Number"
              value={invoiceForm.invoiceNumber}
              editable={false}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Issue Date (YYYY-MM-DD)"
              value={invoiceForm.issueDate}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, issueDate: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Due Date (YYYY-MM-DD)"
              value={invoiceForm.dueDate}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, dueDate: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Invoice Description *"
              value={invoiceForm.description}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, description: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Reference / PO Number"
              value={invoiceForm.reference}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, reference: text })}
            />
          </View>
          
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Client Information</Text>
            <TextInput
              style={styles.formInput}
              placeholder="Client Name *"
              value={invoiceForm.clientName}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, clientName: text })}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Client Email"
              value={invoiceForm.clientEmail}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, clientEmail: text })}
              keyboardType="email-address"
            />
            <TextInput
              style={styles.formInput}
              placeholder="Client Company *"
              value={invoiceForm.clientCompany}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, clientCompany: text })}
            />
            <TextInput
              style={[styles.formInput, styles.textArea]}
              placeholder="Client Address"
              value={invoiceForm.clientAddress}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, clientAddress: text })}
              multiline
              numberOfLines={3}
            />
            <TextInput
              style={styles.formInput}
              placeholder="Client VAT Number"
              value={invoiceForm.clientVAT}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, clientVAT: text })}
            />
          </View>
          
          <View style={styles.formSection}>
            <View style={styles.formSectionHeader}>
              <Text style={styles.formSectionTitle}>Invoice Items</Text>
              <TouchableOpacity 
                style={styles.addButton}
                onPress={addInvoiceItem}
              >
                <MaterialCommunityIcons name="plus" size={20} color="#0e4e96" />
                <Text style={styles.addButtonText}>Add Item</Text>
              </TouchableOpacity>
            </View>
            
            {invoiceForm.items.map((item, index) => (
              <View key={item.id} style={styles.itemForm}>
                <View style={styles.itemHeader}>
                  <Text style={styles.itemTitle}>Item {index + 1}</Text>
                  {invoiceForm.items.length > 1 && (
                    <TouchableOpacity 
                      onPress={() => removeInvoiceItem(item.id)}
                      style={styles.removeButton}
                    >
                      <MaterialCommunityIcons name="close" size={16} color="#EF4444" />
                    </TouchableOpacity>
                  )}
                </View>
                <TextInput
                  style={styles.formInput}
                  placeholder="Description"
                  value={item.description}
                  onChangeText={(text) => updateInvoiceItem(item.id, 'description', text)}
                />
                <View style={styles.itemRow}>
                  <TextInput
                    style={[styles.formInput, styles.itemInput]}
                    placeholder="Qty"
                    value={item.quantity.toString()}
                    onChangeText={(text) => updateInvoiceItem(item.id, 'quantity', text)}
                    keyboardType="numeric"
                  />
                  <TextInput
                    style={[styles.formInput, styles.itemInput]}
                    placeholder="Unit"
                    value={item.unit || 'hr'}
                    onChangeText={(text) => updateInvoiceItem(item.id, 'unit', text)}
                  />
                  <TextInput
                    style={[styles.formInput, styles.itemInput]}
                    placeholder="Rate"
                    value={item.rate.toString()}
                    onChangeText={(text) => updateInvoiceItem(item.id, 'rate', text)}
                    keyboardType="numeric"
                  />
                  <View style={[styles.formInput, styles.itemInput, styles.amountDisplay]}>
                    <Text style={styles.amountText}>{formatCurrency(item.amount)}</Text>
                  </View>
                </View>
              </View>
            ))}
          </View>
          
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Tax & Discount</Text>
            <View style={styles.taxDiscountRow}>
              <TextInput
                style={[styles.formInput, styles.taxInput]}
                placeholder="Tax Rate (%)"
                value={invoiceForm.taxRate}
                onChangeText={(text) => setInvoiceForm({ ...invoiceForm, taxRate: text })}
                keyboardType="numeric"
              />
              <TextInput
                style={[styles.formInput, styles.taxInput]}
                placeholder="Discount (%)"
                value={invoiceForm.discountRate}
                onChangeText={(text) => setInvoiceForm({ ...invoiceForm, discountRate: text })}
                keyboardType="numeric"
              />
            </View>
            <TextInput
              style={styles.formInput}
              placeholder="Payment Method"
              value={invoiceForm.paymentMethod}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, paymentMethod: text })}
            />
          </View>
          
          <View style={styles.formSection}>
            <Text style={styles.formSectionTitle}>Notes</Text>
            <TextInput
              style={[styles.formInput, styles.textArea]}
              placeholder="Invoice notes and payment terms"
              value={invoiceForm.notes}
              onChangeText={(text) => setInvoiceForm({ ...invoiceForm, notes: text })}
              multiline
              numberOfLines={3}
            />
          </View>
          
          <View style={styles.invoiceSummary}>
            <Text style={styles.summaryTitle}>Invoice Summary</Text>
            {(() => {
              const totals = calculateInvoiceTotals(
                invoiceForm.items,
                parseFloat(invoiceForm.taxRate) || 0,
                parseFloat(invoiceForm.discountRate) || 0
              );
              return (
                <View>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Subtotal:</Text>
                    <Text style={styles.summaryValue}>{formatCurrency(totals.subtotal)}</Text>
                  </View>
                  {totals.discountAmount > 0 && (
                    <View style={styles.summaryRow}>
                      <Text style={styles.summaryLabel}>Discount:</Text>
                      <Text style={styles.summaryValue}>-{formatCurrency(totals.discountAmount)}</Text>
                    </View>
                  )}
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>VAT ({invoiceForm.taxRate}%):</Text>
                    <Text style={styles.summaryValue}>{formatCurrency(totals.taxAmount)}</Text>
                  </View>
                  <View style={[styles.summaryRow, styles.totalRow]}>
                    <Text style={styles.totalLabel}>Total:</Text>
                    <Text style={styles.totalValue}>{formatCurrency(totals.total)}</Text>
                  </View>
                </View>
              );
            })()}
          </View>

          <View style={styles.companyPreviewSection}>
            <Text style={styles.previewTitle}>Will be issued by:</Text>
            <View style={styles.companyPreview}>
              {companyProfile.logo ? (
                <Image 
                  source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} 
                  style={styles.previewLogo}
                />
              ) : (
                <View style={styles.previewLogoPlaceholder}>
                  <MaterialCommunityIcons name="office-building" size={24} color="#9CA3AF" />
                </View>
              )}
              <View style={styles.companyPreviewDetails}>
                <Text style={styles.previewCompanyName}>{companyProfile.name}</Text>
                <Text style={styles.previewCompanyAddress}>{companyProfile.headquarters}</Text>
                <Text style={styles.previewCompanyVAT}>VAT: {companyProfile.vatNumber}</Text>
              </View>
            </View>
          </View>
        </ScrollView>
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.cancelButton]}
            onPress={() => setShowCreateModal(false)}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.modalButton, styles.saveButton]}
            onPress={handleSaveInvoice}
          >
            <Text style={styles.saveButtonText}>Save Invoice</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );

  const renderInvoiceModal = () => (
    <Modal
      visible={showInvoiceModal}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={() => {
        setShowInvoiceModal(false);
        setSelectedInvoice(null);
      }}
    >
      <View style={styles.modalContainer}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalTitle}>
            {selectedInvoice ? selectedInvoice.invoiceNumber : 'Invoice Details'}
          </Text>
          <TouchableOpacity 
            style={styles.modalCloseButton}
            onPress={() => {
              setShowInvoiceModal(false);
              setSelectedInvoice(null);
            }}
          >
            <MaterialCommunityIcons name="close" size={24} color="#6B7280" />
          </TouchableOpacity>
        </View>
        
        {selectedInvoice && (
          <ScrollView style={styles.modalContent}>
            <View style={styles.invoicePreview}>
              <View style={styles.invoicePreviewCompany}>
                {companyProfile.logo ? (
                  <Image 
                    source={{ uri: (companyProfile.logo && companyProfile.logo.startsWith('http')) ? companyProfile.logo : undefined }} 
                    style={styles.previewCompanyLogo}
                  />
                ) : (
                  <View style={styles.previewCompanyLogoPlaceholder}>
                    <MaterialCommunityIcons name="office-building" size={30} color="#9CA3AF" />
                  </View>
                )}
                <View style={styles.invoicePreviewCompanyInfo}>
                  <Text style={styles.invoicePreviewCompanyName}>{companyProfile.name}</Text>
                  <Text style={styles.invoicePreviewCompanyAddress}>{companyProfile.headquarters}</Text>
                  <Text style={styles.invoicePreviewCompanyVAT}>VAT: {companyProfile.vatNumber}</Text>
                </View>
              </View>

              <View style={styles.invoicePreviewHeader}>
                <Text style={styles.invoicePreviewNumber}>{selectedInvoice.invoiceNumber}</Text>
                <View style={[styles.statusBadge, { backgroundColor: getStatusColor(selectedInvoice.status) }]}>
                  <MaterialCommunityIcons 
                    name={getStatusIcon(selectedInvoice.status)} 
                    size={12} 
                    color="#FFFFFF" 
                  />
                  <Text style={styles.statusText}>{selectedInvoice.status}</Text>
                </View>
              </View>
              
              <View style={styles.invoicePreviewSection}>
                <Text style={styles.invoicePreviewSectionTitle}>Bill To</Text>
                <Text style={styles.invoicePreviewText}>
                  {selectedInvoice.clientName}{'\n'}
                  {selectedInvoice.clientCompany}{'\n'}
                  {selectedInvoice.clientEmail}{'\n'}
                  {selectedInvoice.clientAddress}
                  {selectedInvoice.clientVAT ? `\nVAT: ${selectedInvoice.clientVAT}` : ''}
                </Text>
              </View>
              
              <View style={styles.invoicePreviewSection}>
                <Text style={styles.invoicePreviewSectionTitle}>Invoice Details</Text>
                <Text style={styles.invoicePreviewText}>
                  Description: {selectedInvoice.description}{'\n'}
                  Issue Date: {formatDate(selectedInvoice.issueDate)}{'\n'}
                  Due Date: {formatDate(selectedInvoice.dueDate)}{'\n'}
                  <Text>{selectedInvoice.paidDate && `Paid Date: ${formatDate(selectedInvoice.paidDate)}\n`}</Text>
                  <Text>{selectedInvoice.reference && `Reference: ${selectedInvoice.reference}`}</Text>
                </Text>
              </View>
              
              <View style={styles.invoicePreviewSection}>
                <Text style={styles.invoicePreviewSectionTitle}>Invoice Items</Text>
                {selectedInvoice.items.map((item, index) => (
                  <View key={index} style={styles.invoiceItemPreview}>
                    <Text style={styles.itemPreviewDescription}>{item.description}</Text>
                    <View style={styles.itemPreviewDetails}>
                      <Text style={styles.itemPreviewQty}>Qty: {item.quantity} {item.unit || 'hr'}</Text>
                      <Text style={styles.itemPreviewRate}>Rate: {formatCurrency(item.rate)}</Text>
                      <Text style={styles.itemPreviewAmount}>Amount: {formatCurrency(item.amount)}</Text>
                    </View>
                  </View>
                ))}
              </View>
              
              <View style={styles.invoicePreviewSection}>
                <Text style={styles.invoicePreviewSectionTitle}>Invoice Summary</Text>
                <View style={styles.invoiceSummaryPreview}>
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>Subtotal:</Text>
                    <Text style={styles.summaryValue}>{formatCurrency(selectedInvoice.subtotal)}</Text>
                  </View>
                  {selectedInvoice.discountAmount > 0 && (
                    <View style={styles.summaryRow}>
                      <Text style={styles.summaryLabel}>Discount ({selectedInvoice.discountRate}%):</Text>
                      <Text style={styles.summaryValue}>-{formatCurrency(selectedInvoice.discountAmount)}</Text>
                    </View>
                  )}
                  <View style={styles.summaryRow}>
                    <Text style={styles.summaryLabel}>VAT ({selectedInvoice.taxRate}%):</Text>
                    <Text style={styles.summaryValue}>{formatCurrency(selectedInvoice.taxAmount)}</Text>
                  </View>
                  <View style={[styles.summaryRow, styles.totalRow]}>
                    <Text style={styles.totalLabel}>Total:</Text>
                    <Text style={styles.totalValue}>{formatCurrency(selectedInvoice.total)}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.invoicePreviewSection}>
                <Text style={styles.invoicePreviewSectionTitle}>Payment Information</Text>
                <View style={styles.paymentInfo}>
                  <Text style={styles.paymentInfoText}>
                    Bank: {companyProfile.bankName}{'\n'}
                    Account: {companyProfile.bankAccount}{'\n'}
                    Sort Code: {companyProfile.bankSortCode}{'\n'}
                    IBAN: {companyProfile.bankIBAN}{'\n'}
                    SWIFT: {companyProfile.bankSWIFT}
                  </Text>
                </View>
              </View>
              
              {selectedInvoice.notes && (
                <View style={styles.invoicePreviewSection}>
                  <Text style={styles.invoicePreviewSectionTitle}>Notes</Text>
                  <Text style={styles.invoicePreviewText}>{selectedInvoice.notes}</Text>
                </View>
              )}

              <View style={styles.invoicePreviewFooter}>
                <Text style={styles.invoicePreviewFooterText}>
                  {companyProfile.invoiceFooter || 'Thank you for your business.'}
                </Text>
              </View>
            </View>
          </ScrollView>
        )}
        
        <View style={styles.modalFooter}>
          <TouchableOpacity 
            style={[styles.modalButton, styles.exportButton]}
            onPress={() => generateAndPrintPDF(selectedInvoice)}
          >
            <MaterialCommunityIcons name="file-pdf-box" size={20} color="#FFFFFF" />
            <Text style={styles.exportButtonText}>PDF</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.modalButton, styles.shareButton]}
            onPress={() => shareInvoiceLink(selectedInvoice)}
          >
            <MaterialCommunityIcons name="share-variant" size={20} color="#FFFFFF" />
            <Text style={styles.shareButtonText}>Share</Text>
          </TouchableOpacity>
          {selectedInvoice?.status === 'draft' && (
            <TouchableOpacity 
              style={[styles.modalButton, styles.sendButton]}
              onPress={() => {
                handleSendInvoice(selectedInvoice.id);
                setShowInvoiceModal(false);
                setSelectedInvoice(null);
              }}
            >
              <MaterialCommunityIcons name="send" size={20} color="#FFFFFF" />
              <Text style={styles.sendButtonText}>Send</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );

  if (isInitialLoading) {
    return (
      <SafeAreaView style={styles.loadingContainer}>
        <ActivityIndicator size={48} color="#0e4e96" />
        <Text style={styles.loadingText}>Loading Invoice System...</Text>
        <Text style={styles.loadingSubtext}>{companyProfile.name}</Text>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
      
      {renderHeader()}
      {renderTabs()}
      {renderContent()}
      {renderCreateModal()}
      {renderInvoiceModal()}
      {renderOCRSModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F8FAFC',
  },
  
  loadingText: {
    marginTop: 20,
    fontSize: 18,
    color: '#1F2937',
    fontWeight: '600',
  },
  
  loadingSubtext: {
    marginTop: 8,
    fontSize: 14,
    color: '#6B7280',
  },
  
  // Header styles
  header: {
    paddingTop: 12,
    paddingBottom: 16,
    paddingHorizontal: 16,
  },
  
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  
  headerCompanyInfo: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  headerLogo: {
    width: 36,
    height: 36,
    borderRadius: 8,
    marginRight: 10,
  },
  
  headerLogoPlaceholder: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10,
  },
  
  headerCompanyDetails: {
    alignItems: 'flex-start',
  },
  
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#FFFFFF',
  },
  
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 2,
  },
  
  headerButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  headerButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  headerButtonLeft: {
    marginRight: 8,
  },
  
  // Tabs styles
  tabsContainer: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  tabsContent: {
    paddingHorizontal: 16,
  },
  
  tab: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 8,
    borderRadius: 20,
  },
  
  activeTab: {
    backgroundColor: '#EBF4FF',
  },
  
  tabText: {
    fontSize: 14,
    color: '#6B7280',
    marginLeft: 8,
    fontWeight: '500',
  },
  
  activeTabText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  
  // Content styles
  contentContainer: {
    flex: 1,
  },
  
  listContainer: {
    padding: 16,
    flexGrow: 1,
  },
  
  // Search and filter styles
  searchContainer: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  searchBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F3F4F6',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
  },
  
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#1F2937',
    marginLeft: 8,
  },
  
  filterContainer: {
    flexDirection: 'row',
  },
  
  filterButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    marginRight: 8,
  },
  
  activeFilterButton: {
    backgroundColor: '#0e4e96',
  },
  
  filterButtonText: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  activeFilterButtonText: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  
  // Stats styles
  statsContainer: {
    padding: 16,
  },
  
  row: {
    justifyContent: 'space-between',
  },
  
  statCard: {
    width: (width - 48) / 2,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    alignItems: 'center',
  },
  
  statIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  statValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  statTitle: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
  },
  
  // Invoice card styles
  invoiceCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  invoiceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  
  invoiceInfo: {
    flex: 1,
  },
  
  invoiceNumber: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
    marginBottom: 4,
  },
  
  invoiceClient: {
    fontSize: 14,
    color: '#1F2937',
    marginBottom: 4,
  },
  
  invoiceDescription: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  invoiceStatus: {
    alignItems: 'flex-end',
  },
  
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  statusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
    textTransform: 'capitalize',
  },
  
  invoiceDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  
  invoiceDetail: {
    flex: 1,
  },
  
  invoiceDetailLabel: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 4,
  },
  
  invoiceDetailValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  invoiceActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
  },
  
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    marginLeft: 8,
    marginBottom: 4,
  },
  
  actionButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 4,
  },
  
  // Empty state styles
  emptyState: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
  },
  
  emptyStateTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginTop: 16,
    marginBottom: 8,
  },
  
  emptyStateText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingHorizontal: 32,
  },
  
  emptyText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
    paddingVertical: 20,
  },
  
  // Template styles
  templatesListContainer: {
    paddingVertical: 16,
  },
  
  categorySection: {
    marginBottom: 24,
  },
  
  categoryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    marginBottom: 12,
  },
  
  categoryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginLeft: 8,
    flex: 1,
  },
  
  categoryCount: {
    fontSize: 14,
    color: '#6B7280',
    fontWeight: '500',
  },
  
  templatesRow: {
    paddingHorizontal: 16,
  },
  
  templateCard: {
    width: 280,
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginRight: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  templateIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#EBF4FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  templateHeader: {
    marginBottom: 8,
  },
  
  templateName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  templateDescription: {
    fontSize: 13,
    color: '#6B7280',
    marginBottom: 12,
  },
  
  templateItems: {
    marginBottom: 12,
  },
  
  templateItemsTitle: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  templateItem: {
    fontSize: 11,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  templateFooter: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 8,
  },
  
  templateTax: {
    fontSize: 11,
    color: '#0e4e96',
    fontWeight: '500',
    marginBottom: 2,
  },
  
  templateDiscount: {
    fontSize: 11,
    color: '#10B981',
    fontWeight: '500',
    marginBottom: 2,
  },
  
  templatePayment: {
    fontSize: 11,
    color: '#8B5CF6',
    fontWeight: '500',
  },
  
  // Payments styles
  paymentsSection: {
    backgroundColor: '#FFFFFF',
    margin: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 16,
  },
  
  paymentCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#10B981',
  },
  
  paymentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  
  paymentInvoice: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  paymentAmount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#10B981',
  },
  
  paymentClient: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  paymentDate: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  paymentMethod: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
  },
  
  pendingCard: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#F59E0B',
  },
  
  pendingHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  
  pendingInvoice: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  pendingAmount: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#F59E0B',
  },
  
  pendingClient: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  pendingDue: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 8,
  },
  
  pendingStatus: {
    alignSelf: 'flex-start',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 12,
  },
  
  pendingStatusText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#FFFFFF',
    textTransform: 'capitalize',
  },
  
  // Analytics styles
  analyticsSection: {
    backgroundColor: '#FFFFFF',
    margin: 16,
    padding: 20,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  statusDistribution: {
    flexDirection: 'column',
  },
  
  statusItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  statusIndicator: {
    width: 12,
    height: 12,
    borderRadius: 6,
    marginRight: 12,
  },
  
  statusLabel: {
    flex: 1,
    fontSize: 14,
    color: '#6B7280',
    textTransform: 'capitalize',
  },
  
  statusCount: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  revenueBreakdown: {
    flexDirection: 'column',
  },
  
  revenueItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  
  revenueLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  revenueAmount: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  // Modal styles
  modalContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
  },
  
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
  },
  
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1F2937',
    flex: 1,
  },
  
  modalCloseButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  modalHeaderButtons: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  modalHeaderButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  
  ocrHeaderButton: {
    backgroundColor: '#EBF4FF',
  },
  
  modalContent: {
    flex: 1,
    padding: 16,
  },
  
  modalFooter: {
    flexDirection: 'row',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  
  modalButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderRadius: 8,
    marginHorizontal: 4,
  },
  
  cancelButton: {
    backgroundColor: '#F3F4F6',
  },
  
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#6B7280',
  },
  
  saveButton: {
    backgroundColor: '#0e4e96',
  },
  
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  
  exportButton: {
    backgroundColor: '#6B7280',
  },
  
  exportButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  shareButton: {
    backgroundColor: '#8B5CF6',
  },
  
  shareButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  sendButton: {
    backgroundColor: '#0e4e96',
  },
  
  sendButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  cameraButton: {
    backgroundColor: '#0e4e96',
  },
  
  cameraButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  
  // Company Preview in Create Modal
  companyPreviewSection: {
    marginTop: 16,
    marginBottom: 24,
    padding: 16,
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  
  previewTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6B7280',
    marginBottom: 12,
  },
  
  companyPreview: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  
  previewLogo: {
    width: 48,
    height: 48,
    borderRadius: 8,
    marginRight: 12,
  },
  
  previewLogoPlaceholder: {
    width: 48,
    height: 48,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  
  companyPreviewDetails: {
    flex: 1,
  },
  
  previewCompanyName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0e4e96',
    marginBottom: 4,
  },
  
  previewCompanyAddress: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  previewCompanyVAT: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
  },
  
  // OCR styles
  ocrContainer: {
    alignItems: 'center',
    padding: 20,
  },
  
  ocrIllustration: {
    width: 120,
    height: 120,
    borderRadius: 60,
    backgroundColor: '#EBF4FF',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 20,
  },
  
  ocrTitle: {
    fontSize: 22,
    fontWeight: 'bold',
    color: '#1F2937',
    marginBottom: 12,
  },
  
  ocrDescription: {
    fontSize: 16,
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: 24,
  },
  
  ocrFeatures: {
    width: '100%',
    marginBottom: 24,
  },
  
  ocrFeature: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  ocrFeatureText: {
    fontSize: 15,
    color: '#1F2937',
    marginLeft: 12,
  },
  
  ocrProcessing: {
    alignItems: 'center',
    padding: 20,
  },
  
  ocrProcessingText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1F2937',
    marginTop: 16,
    marginBottom: 8,
  },
  
  ocrProcessingSubtext: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  ocrSuccessBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#D1FAE5',
    padding: 12,
    marginHorizontal: 16,
    marginTop: 8,
    borderRadius: 8,
  },
  
  ocrSuccessText: {
    fontSize: 14,
    color: '#065F46',
    marginLeft: 8,
    flex: 1,
  },
  
  // Form styles
  formSection: {
    marginBottom: 24,
  },
  
  formSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  
  formSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  
  formInput: {
    borderWidth: 1,
    borderColor: '#D1D5DB',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
    color: '#1F2937',
    backgroundColor: '#FFFFFF',
    marginBottom: 12,
  },
  
  disabledInput: {
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
  },
  
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#0e4e96',
  },
  
  addButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0e4e96',
    marginLeft: 4,
  },
  
  itemForm: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
  },
  
  itemHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  itemTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  removeButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FEE2E2',
    justifyContent: 'center',
    alignItems: 'center',
  },
  
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  
  itemInput: {
    flex: 1,
    marginRight: 8,
    minWidth: 80,
  },
  
  amountDisplay: {
    justifyContent: 'center',
    backgroundColor: '#F3F4F6',
    marginRight: 0,
  },
  
  amountText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  taxDiscountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  
  taxInput: {
    flex: 1,
    marginRight: 8,
  },
  
  invoiceSummary: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  
  summaryTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 12,
  },
  
  summaryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  
  summaryLabel: {
    fontSize: 14,
    color: '#6B7280',
  },
  
  summaryValue: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  totalRow: {
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
    paddingTop: 8,
    marginTop: 8,
  },
  
  totalLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1F2937',
  },
  
  totalValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  
  // Invoice preview styles
  invoicePreview: {
    backgroundColor: '#F9FAFB',
    borderRadius: 8,
    padding: 16,
  },
  
  invoicePreviewCompany: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  
  previewCompanyLogo: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 16,
  },
  
  previewCompanyLogoPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 8,
    backgroundColor: '#F3F4F6',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  
  invoicePreviewCompanyInfo: {
    flex: 1,
  },
  
  invoicePreviewCompanyName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
    marginBottom: 4,
  },
  
  invoicePreviewCompanyAddress: {
    fontSize: 12,
    color: '#6B7280',
    marginBottom: 2,
  },
  
  invoicePreviewCompanyVAT: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
  },
  
  invoicePreviewHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  
  invoicePreviewNumber: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  
  invoicePreviewSection: {
    marginBottom: 20,
  },
  
  invoicePreviewSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    paddingBottom: 4,
  },
  
  invoicePreviewText: {
    fontSize: 14,
    color: '#6B7280',
    lineHeight: 20,
  },
  
  invoiceItemPreview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 12,
    marginBottom: 8,
  },
  
  itemPreviewDescription: {
    fontSize: 14,
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: 4,
  },
  
  itemPreviewDetails: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
  },
  
  itemPreviewQty: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  itemPreviewRate: {
    fontSize: 12,
    color: '#6B7280',
  },
  
  itemPreviewAmount: {
    fontSize: 12,
    fontWeight: '600',
    color: '#1F2937',
  },
  
  invoiceSummaryPreview: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 12,
  },
  
  paymentInfo: {
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    padding: 12,
  },
  
  paymentInfoText: {
    fontSize: 13,
    color: '#1F2937',
    lineHeight: 20,
  },
  
  invoicePreviewFooter: {
    marginTop: 20,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#E5E7EB',
  },
  
  invoicePreviewFooterText: {
    fontSize: 12,
    color: '#6B7280',
    textAlign: 'center',
    fontStyle: 'italic',
  },

  // New styles for sync and offline indicators
  syncIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    padding: 8,
    borderRadius: 8,
  },
  syncText: {
    color: '#FFFFFF',
    marginLeft: 8,
    fontSize: 12,
    fontWeight: '500',
  },
  offlineIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 8,
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    padding: 8,
    borderRadius: 8,
  },
  offlineText: {
    color: '#FFFFFF',
    marginLeft: 8,
    fontSize: 12,
    fontWeight: '500',
  },
});